// AgentChatDock — persistent, multi-conversation chat panel for sales agents.
//
// Mounted once in the tenant app shell (BasePage) so agents can handle ecom storefront
// chats from any screen. Owns the agent Socket.IO connection (role=ecom_agent); the Live
// Visitors board reuses the same singleton socket.
//
// Conversations are WhatsApp-style: ONE row/thread per shopper, not per chat session.
// The backend still creates a new EcomChatSession every time a closed chat is reopened,
// but the dock folds all of a shopper's sessions into a single conversation keyed by
// identity (customer_id → welcome-form phone → visitor_id) and the server returns the
// cross-session thread history on accept.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  connectAgentSocket,
  acceptChat,
  sendAgentMessage,
  closeChat,
  disconnectAgentSocket,
  initiateChat,
  initiateChatWithCustomer,
  transferChat,
  releaseChat,
  startCall,
  sendCallOffer,
  sendCallIce,
  notifyCallConnected,
  endCall,
} from "./agentSocket";
import {
  fetchSessions,
  fetchThread,
  fetchContext,
  fetchShoppers,
  fetchAgents,
  fetchCustomerContext,
  searchCustomers as searchCustomersApi,
} from "./ecomChatApi";
import { playPing, ensureNotifyPermission, notifyBrowser, flashTitle, stopTitleFlash } from "./chatNotify";
import ChatEmojiPicker from "./ChatEmojiPicker";
import ChatAttachment from "./ChatAttachment";
import { ORDER_STATUS as ORDER_STATUS_CONST } from "../Common/constants";
import { MESSAGE_TYPE, FILE_ACCEPT, MAX_FILE_BYTES, isAllowedMime, typeForMime, uploadChatFile } from "./chatMedia";
import { getSettingKey } from "../Common/utils";
import { createVoicePeer, callSupported } from "./voiceCall";

// mm:ss for the live in-call timer.
function fmtCallTime(secs) {
  const s = Math.max(0, Math.floor(secs || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
// Human reason for why a call couldn't start / ended early.
function callUnavailableText(reason) {
  if (reason === "offline") return "The shopper just went offline — they can only be called while on the site.";
  if (reason === "busy") return "A call is already in progress for this chat.";
  if (reason === "not_owner") return "You no longer own this chat.";
  if (reason === "disabled") return "Voice calls are disabled.";
  if (reason === "declined") return "The shopper declined the call.";
  if (reason === "no_answer") return "No answer.";
  if (reason === "mic_denied") return "Microphone access was blocked.";
  return "The call ended.";
}

const STATUS = { QUEUED: 1, OPEN: 2, CLOSED: 3, OFFLINE: 4 };
const SENDER = { CUSTOMER: 1, AGENT: 2, SYSTEM: 3 };
// Long safety-net re-sync only — primary updates are socket-event-driven.
const PRESENCE_REFRESH_MS = 120000;

function sideOf(t) {
  if (t === SENDER.AGENT) return "me";
  if (t === SENDER.SYSTEM) return "system";
  return "customer";
}
function toMsg(m) {
  return {
    id: m.id,
    session_id: m.session_id,
    side: sideOf(m.sender_type),
    body: m.body,
    message_type: m.message_type || MESSAGE_TYPE.TEXT,
    attachment: m.attachment || null,
    ts: m.created_at,
  };
}
// Server thread history spans multiple sessions; drop a subtle divider where one chat
// ended and the next began so the agent can still see the engagement boundaries.
function historyToMsgs(history) {
  return (history || []).map((m) => toMsg(m));
}
// --- Conversation identity: one thread per shopper, WhatsApp-style. ---
// Last 10 digits, so "+1 (555) 010-7777" and "555-010-7777" land in the same thread.
function phoneKeyOf(s) {
  const digits = (s && s.visitor_phone ? String(s.visitor_phone) : "").replace(/\D/g, "").slice(-10);
  return digits || null;
}
// Strongest identity wins: verified customer id, then the welcome-form phone number
// (visitor_id resets with cleared storage / a new device, phone follows the person),
// then the device-scoped visitor id.
function keyOf(s) {
  if (s.customer_id) return `c:${s.customer_id}`;
  const p = phoneKeyOf(s);
  if (p) return `p:${p}`;
  if (s.visitor_id) return `v:${s.visitor_id}`;
  return `s:${s.id}`;
}
// Do sessions a (incoming) and b (already in a conversation) belong to the same shopper?
// Mirrors the server's thread-identity rules: two DIFFERENT logged-in customers never
// merge (shared computer / shared phone), and a guest session never absorbs a logged-in
// customer's chats via the device id alone.
function sameShopper(a, b) {
  if (a.customer_id && b.customer_id) return String(a.customer_id) === String(b.customer_id);
  const pa = phoneKeyOf(a);
  if (pa && pa === phoneKeyOf(b)) return true;
  if (!a.customer_id && b.customer_id) return false;
  return !!a.visitor_id && a.visitor_id === b.visitor_id;
}
const KEY_RANK = { c: 0, p: 1, v: 2, s: 3 };
function strongestKey(sessions) {
  let best = null;
  let bestRank = 99;
  sessions.forEach((s) => {
    const k = keyOf(s);
    const r = KEY_RANK[k[0]];
    if (r < bestRank) {
      bestRank = r;
      best = k;
    }
  });
  return best;
}
// The newest session drives the conversation's status, preview and message target.
function latestOf(conv) {
  let best = null;
  Object.values((conv && conv.sessions) || {}).forEach((s) => {
    if (!best || (s.id || 0) > (best.id || 0)) best = s;
  });
  return best;
}
// Thread UI (composer / Reopen / Closed) must follow the newest session in the transcript,
// not only conv.sessions — reopen can append history with a new session_id before the
// sessions map catches up (missed ecom_chat_accepted on this tab).
function threadLatestSession(conv) {
  const fromSessions = latestOf(conv);
  if (!conv) return fromSessions;
  let maxMsgSid = 0;
  (conv.messages || []).forEach((m) => {
    const sid = typeof m.session_id === "number" ? m.session_id : parseInt(m.session_id, 10);
    if (sid && sid > maxMsgSid) maxMsgSid = sid;
  });
  const bestId = Math.max(fromSessions?.id || 0, maxMsgSid);
  if (!bestId) return fromSessions;
  const fromMap = conv.sessions[bestId] || conv.sessions[String(bestId)];
  if (fromMap) return fromMap;
  if (fromSessions && bestId > (fromSessions.id || 0) && fromSessions.status === STATUS.CLOSED) {
    return { ...fromSessions, id: bestId, status: STATUS.OPEN };
  }
  return fromSessions;
}
// The session that best identifies the shopper — same ranking the thread is KEYED by
// (verified customer > welcome-form phone > device visitor id). The label must follow
// the key, not the newest session: otherwise one signed-out chat from the same browser
// renames a known customer's whole thread to "Guest xxxxxx". Within a rank, prefer the
// newest session that actually carries a name (customer_name / visitor_name).
export function identityOf(conv) {
  let best = null;
  let bestScore = null;
  Object.values((conv && conv.sessions) || {}).forEach((s) => {
    const score = [
      KEY_RANK[keyOf(s)[0]],
      s.customer_name || s.visitor_name ? 0 : 1,
      -(s.id || 0),
    ];
    if (
      !bestScore ||
      score[0] < bestScore[0] ||
      (score[0] === bestScore[0] &&
        (score[1] < bestScore[1] || (score[1] === bestScore[1] && score[2] < bestScore[2])))
    ) {
      bestScore = score;
      best = s;
    }
  });
  return best;
}
// REST session-list rows and socket patches can be partial. Never wipe ownership the dock
// already learned from ecom_chat_accepted (common after refresh + a live message).
function mergeSessionPayload(existing, incoming) {
  if (!incoming) return existing || null;
  const merged = { ...(existing || {}), ...incoming };
  if (existing) {
    if (incoming.assigned_agent_id == null && existing.assigned_agent_id != null) {
      merged.assigned_agent_id = existing.assigned_agent_id;
    }
    if (!incoming.assigned_agent_name && existing.assigned_agent_name) {
      merged.assigned_agent_name = existing.assigned_agent_name;
    }
    if (incoming.status == null && existing.status != null) merged.status = existing.status;
    if (existing.status === STATUS.OPEN && incoming.status === STATUS.QUEUED) {
      merged.status = STATUS.OPEN;
    }
  }
  return merged;
}
// Merge a session payload into the conversation map. Folds together every conversation
// that belongs to the same shopper (a guest thread once they log in, or a new visitor_id
// matched by phone) and flushes messages that arrived before their session payload did.
// Merged-away keys are recorded in keyAliases so an active selection can follow.
function upsertIntoConvos(prev, sess, patch, pendingBySession, keyAliases) {
  const next = { ...prev };
  const sources = Object.keys(next).filter((k) => {
    const c = next[k];
    if (c.sessions[sess.id]) return true;
    return Object.values(c.sessions).some((s) => sameShopper(sess, s));
  });

  let conv = { sessions: {}, messages: [], unread: 0 };
  sources.forEach((k) => {
    const src = next[k];
    conv.sessions = { ...conv.sessions, ...src.sessions };
    conv.messages = [...conv.messages, ...src.messages];
    conv.unread += src.unread || 0;
    if (conv.ctx === undefined && src.ctx !== undefined) conv.ctx = src.ctx;
    delete next[k];
  });
  conv.sessions[sess.id] = mergeSessionPayload(conv.sessions[sess.id], sess);

  const targetKey = strongestKey(Object.values(conv.sessions));
  sources.forEach((k) => {
    if (k !== targetKey) keyAliases[k] = targetKey;
  });

  conv = { ...conv, ...patch };

  const buffered = pendingBySession[sess.id];
  if (buffered && buffered.length) {
    const seen = new Set(conv.messages.map((m) => m.id));
    conv.messages = [...conv.messages, ...buffered.filter((m) => !seen.has(m.id))];
    delete pendingBySession[sess.id];
  }

  next[targetKey] = conv;
  return next;
}
// --- Identity helpers: a logged-in customer vs an anonymous storefront visitor. ---
// `is_customer`/`customer_id` is set by the backend only when the shopper's ecom JWT
// decoded successfully — it is never trusted from the client, so it's a reliable signal.
function isCustomer(s) {
  return !!(s && (s.is_customer || s.customer_id));
}
function displayName(s) {
  if (!s) return "Visitor";
  if (isCustomer(s)) return s.customer_name || s.visitor_name || `Customer #${s.customer_id}`;
  return s.visitor_name || `Guest ${(s.visitor_id || "").slice(0, 6)}`;
}
function displayEmail(s) {
  if (!s) return null;
  return s.customer_email || s.visitor_email || null;
}
// B2B shoppers are known by their business; the person's name is secondary.
function businessNameOf(s) {
  return (isCustomer(s) && s.customer_business_name) || null;
}
function NameBlock({ session, style }) {
  const primary = businessNameOf(session) || displayName(session);
  const person = session && (session.customer_name || session.visitor_name);
  return (
    <span style={styles.nameStack}>
      <span style={style} title={primary}>{primary}</span>
      {person && person !== primary && (
        <span style={styles.nameSub} title={person}>{person}</span>
      )}
    </span>
  );
}
// Search results / Shoppers rows carry {name, business_name}; shape them like a session.
function customerAsSession(id, name, businessName) {
  return { customer_id: id, customer_name: name, customer_business_name: businessName, is_customer: true };
}
function initialsOf(s) {
  const name = businessNameOf(s) || displayName(s) || "";
  const parts = name.replace(/^Guest\s+/i, "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
// Does a conversation match the dock search text? Checks the shopper's name, email and
// phone across ALL of the thread's sessions (a thread may hold a guest session plus the
// logged-in one with different identities).
export function convoMatchesSearch(conv, query) {
  const text = (query || "").trim().toLowerCase();
  if (!text) return true;
  return Object.values((conv && conv.sessions) || {}).some((s) =>
    [displayName(s), displayEmail(s), s.visitor_phone, s.customer_name, s.customer_business_name, s.visitor_name]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(text))
  );
}
// How long the agent must pause typing before the dock asks the server for customers.
const CUSTOMER_SEARCH_DEBOUNCE_MS = 300;
const CUSTOMER_SEARCH_MIN_CHARS = 2;

const ORDER_STATUS_LABEL = Object.values(ORDER_STATUS_CONST).reduce((acc, s) => {
  acc[s.value] = s.type2;
  return acc;
}, {});
function money(n, symbol = "") {
  if (n == null || isNaN(n)) return null;
  return `${symbol}${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}
function memberSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}
// --- message timestamps: a small time on every bubble, a date chip when the day flips ---
function fmtTime(ts) {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
function dayLabel(ts) {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return "Today";
  const yesterday = new Date(today.getTime() - 86400000);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
// Date chip shown above `msg` when its calendar day differs from the previous message's
// (and above the first dated message, so a thread always opens with its date context).
function dateChipBetween(prevMsg, msg) {
  if (!msg || !msg.ts) return null;
  const label = dayLabel(msg.ts);
  if (!label) return null;
  if (!prevMsg || !prevMsg.ts) return label;
  const prev = new Date(prevMsg.ts);
  const cur = new Date(msg.ts);
  if (isNaN(prev.getTime())) return label;
  return prev.toDateString() === cur.toDateString() ? null : label;
}
// Compact relative age for the Shoppers tab ("left 2h ago").
function timeAgo(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
// One-line cart contents: "Coke ×2, Sprite ×1 +3 more".
function cartItemsPreview(row) {
  const items = (row && row.items) || [];
  if (!items.length) return null;
  const shown = items.slice(0, 3).map((i) => `${i.name} ×${i.qty}`).join(", ");
  const more = (row.item_count || items.length) - Math.min(3, items.length);
  return more > 0 ? `${shown} +${more} more` : shown;
}

export default function AgentChatDock() {
  const authToken = useSelector((state) => state.auth.authToken);
  const currencySymbol = useSelector((state) => {
    const entity = state.basicSettings?.entity;
    const sym = getSettingKey(entity, "Basic", "currency_symbol");
    if (sym) return sym;
    const type = getSettingKey(entity, "Basic", "currency_type");
    const map = { USD: "$", EUR: "€", GBP: "£", INR: "₹", CAD: "CA$", AUD: "A$" };
    return map[type] || "";
  }) || "";
  const [open, setOpen] = useState(false);
  const [maximized, setMaximized] = useState(false); // expand the dock to a large overlay
  const [convos, setConvos] = useState({}); // identity key -> {sessions: {id: session}, messages, unread, ctx}
  const [presence, setPresence] = useState({}); // visitor_id -> presence record (who's on the storefront)
  const [activeKey, setActiveKey] = useState(null);
  // Dock search: filters the conversation list AND (≥2 chars) asks the server for matching
  // customers so the agent can start a brand-new chat. null = not searching customers.
  const [search, setSearch] = useState("");
  const [customerResults, setCustomerResults] = useState(null);
  // Search result picked for a customer with NO conversation yet: shows a preview pane with
  // an explicit "Initiate chat now" action instead of dropping the agent into chat mode.
  const [draftCustomer, setDraftCustomer] = useState(null);
  const [draftInitiating, setDraftInitiating] = useState(false);
  const [reopenBusy, setReopenBusy] = useState(false);
  const [acceptBusy, setAcceptBusy] = useState(false);
  // Bottom tab: "chats" (conversations) | "shoppers" (online customers + abandoned carts).
  const [tab, setTab] = useState("chats");
  const [shoppers, setShoppers] = useState(null); // {online: [...], abandoned: [...]} | null
  const [shopperInfo, setShopperInfo] = useState(null); // {customer_id, name, cart, loading} | null
  // Who *I* am (ecom_agent_ready handshake) — ownership UI (composer vs "handled by X" vs
  // the Transfer button) needs the agent's own tenant-user id; the client never decodes JWTs.
  const [me, setMe] = useState(null);
  // Transfer modal: online-colleague directory (null = loading), picked target, private note.
  const [transferOpen, setTransferOpen] = useState(false);
  const [directory, setDirectory] = useState(null);
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferNote, setTransferNote] = useState("");
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferError, setTransferError] = useState("");
  // Set when the server reports the chosen target is mid-conversation: {agent_id, name}.
  // Drives the "transfer anyway?" confirmation; the next Transfer click sends force=true.
  const [busyConfirm, setBusyConfirm] = useState(null);
  const [draft, setDraft] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mediaError, setMediaError] = useState("");
  // --- voice call (agent is the caller) ---
  // call: null | { id, sessionId, peerName, status, error } where status is
  //   "ringing" | "connecting" | "active" | "ended".
  const [call, setCall] = useState(null);
  const [callSeconds, setCallSeconds] = useState(0);
  const [callMuted, setCallMuted] = useState(false);
  // sessionId -> shopper name: a "requested a call" chip the shopper raised.
  const [callRequests, setCallRequests] = useState({});
  const callRef = useRef(null);
  callRef.current = call;
  const callPeerRef = useRef(null);
  const callIceServersRef = useRef([]);
  const remoteAudioRef = useRef(null);
  const callTimerRef = useRef(null);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const activeRef = useRef(activeKey);
  activeRef.current = activeKey;
  const openRef = useRef(open);
  openRef.current = open;
  const convosRef = useRef(convos);
  convosRef.current = convos;
  // Messages whose session payload hasn't arrived yet (a new session's first message
  // beats its ecom_chat_incoming), and merged-away conversation keys → surviving key.
  const pendingRef = useRef({});
  const aliasRef = useRef({});
  // Socket handlers close over mount-time state; identity + the in-flight transfer/release
  // session id live in refs so they read current values.
  const meRef = useRef(null);
  const transferSidRef = useRef(null);
  // Trimmed query the dock last sent, so stale search replies are dropped while typing.
  const searchRef = useRef("");

  const upsert = (sess, patch = {}) =>
    setConvos((prev) => upsertIntoConvos(prev, sess, patch, pendingRef.current, aliasRef.current));

  // Find the conversation currently holding a session id (events only carry session_id).
  const convKeyForSession = (prev, sid) => Object.keys(prev).find((k) => prev[k].sessions[sid]);

  // Tear down the active call locally (stop mic, close peer, clear timer + state). When
  // `notifyReason` is set, also tell the server we hung up (skipped when the END came FROM
  // the server, to avoid an echo).
  const clearCall = (notifyReason) => {
    const cur = callRef.current;
    if (notifyReason && cur && cur.id) endCall(cur.sessionId, cur.id, notifyReason);
    if (callPeerRef.current) {
      try {
        callPeerRef.current.close();
      } catch (e) {
        /* noop */
      }
      callPeerRef.current = null;
    }
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    setCall(null);
    setCallSeconds(0);
    setCallMuted(false);
  };

  useEffect(() => {
    if (!authToken) return undefined;
    const sock = connectAgentSocket(authToken);
    // Desktop-notification permission: ask once, lazily; never re-prompts after a denial.
    ensureNotifyPermission();

    const loadSessions = () => {
      fetchSessions()
        .then((res) => {
          const sessions = res?.data?.sessions || [];
          setConvos((prev) =>
            sessions.reduce(
              (acc, s) => upsertIntoConvos(acc, s, {}, pendingRef.current, aliasRef.current),
              prev
            )
          );
        })
        .catch(() => {});
    };
    const loadShoppers = () => {
      fetchShoppers()
        .then((res) => {
          const payload = res?.data;
          if (!payload) return;
          setShoppers({ online: payload.online || [], abandoned: payload.abandoned || [] });
          const map = {};
          (payload.online || []).forEach((s) => {
            if (!s) return;
            const key = s.visitor_id || (s.customer_id ? `cust-${s.customer_id}` : null);
            if (key) map[key] = { ...s, visitor_id: key, customer_id: s.customer_id };
          });
          setPresence((prev) => ({ ...prev, ...map }));
        })
        .catch(() => {});
    };
    const loadActiveThread = () => {
      const ak = activeRef.current;
      if (!ak || !openRef.current) return;
      const conv = convosRef.current[ak];
      if (!conv) return;
      const sess = latestOf(conv);
      if (!sess || !sess.id) return;
      fetchThread(sess.id)
        .then((res) => {
          const { session_id: sid, history } = res?.data || {};
          if (!sid) return;
          setConvos((prev) => {
            const key = convKeyForSession(prev, sid);
            if (!key) return prev;
            const incoming = historyToMsgs(history);
            if (!prev[key].messages.length) {
              return { ...prev, [key]: { ...prev[key], messages: incoming } };
            }
            const existing = prev[key].messages;
            const serverIds = new Set(incoming.map((m) => m.id));
            const ids = new Set(existing.map((m) => m.id));
            const kept = existing.filter((m) => {
              if (typeof m.id !== "string" || !m.id.startsWith("me-")) return true;
              return !incoming.some(
                (s) => s.side === "me" && s.body === m.body && s.message_type === m.message_type
              );
            });
            const keptIds = new Set(kept.map((m) => m.id));
            const fresh = incoming.filter((m) => !keptIds.has(m.id));
            if (!fresh.length && kept.length === existing.length) return prev;
            const merged = [...kept, ...fresh].sort((a, b) => {
              const aNum = typeof a.id === "number" ? a.id : Infinity;
              const bNum = typeof b.id === "number" ? b.id : Infinity;
              return aNum - bNum;
            });
            return { ...prev, [key]: { ...prev[key], messages: merged } };
          });
        })
        .catch(() => {});
    };
    const onConnect = () => {
      loadSessions();
      loadShoppers();
    };
    const onReady = (payload) => {
      meRef.current = payload || null;
      setMe(payload || null);
    };
    const onIncoming = (payload) => {
      const s = payload?.session;
      if (!s) return;
      upsert(s, { unread: openRef.current && activeRef.current === keyOf(s) ? 0 : 1 });
      loadSessions();
      const mine = meRef.current && s.assigned_agent_id === meRef.current.agent_id;
      if (!s.assigned_agent_id || mine) {
        const focused = typeof document.hasFocus === "function" ? document.hasFocus() : true;
        playPing();
        if (!focused) {
          notifyBrowser(
            s.assigned_agent_id ? "New chat message" : "New chat waiting",
            `${displayName(s)}${s.preview ? `: ${s.preview}` : ""}`
          );
          flashTitle(s.assigned_agent_id ? "💬 New message" : "💬 Chat waiting");
        }
      }
    };
    // Ownership/status refresh for a thread we already track — never creates a new row
    // (a claim hides the queued entry from non-owners; a transfer must not re-list it).
    const applySessionPatch = (s) =>
      setConvos((prev) => {
        const key = convKeyForSession(prev, s.id);
        if (!key) return prev;
        const conv = prev[key];
        return {
          ...prev,
          [key]: {
            ...conv,
            sessions: { ...conv.sessions, [s.id]: mergeSessionPayload(conv.sessions[s.id], s) },
          },
        };
      });
    const onUpdated = (payload) => {
      const s = payload?.session;
      if (!s) return;
      applySessionPatch(s);
      // The release we asked for landed (owner cleared) — the modal's job is done.
      if (transferSidRef.current === s.id && !s.assigned_agent_id) {
        transferSidRef.current = null;
        setTransferOpen(false);
        setTransferBusy(false);
      }
    };
    const onTransferredIn = (payload) => {
      const s = payload?.session;
      if (!s) return;
      const fromName = payload?.from_agent?.name;
      upsert(s, {
        messages: historyToMsgs(payload.history),
        unread: openRef.current && activeRef.current === keyOf(s) ? 0 : 1,
        transferNote: payload?.note ? { from: fromName, text: payload.note } : undefined,
      });
      setOpen(true);
      setTab("chats");
      // Surface it, but never yank the agent out of a thread they're mid-conversation in —
      // the composer's draft is shared, so a forced switch could misroute a typed reply.
      if (!activeRef.current) setActiveKey(keyOf(s));
      playPing();
      notifyBrowser("Chat transferred to you", `${fromName || "A colleague"} handed you ${displayName(s)}`);
      flashTitle("💬 Chat transferred to you");
    };
    const onTransferredOut = (payload) => {
      const s = payload?.session;
      if (!s) return;
      applySessionPatch(s);
      transferSidRef.current = null;
      setTransferOpen(false);
      setTransferBusy(false);
      setTransferError("");
    };
    const onTransferFailed = (payload) => {
      const reason = payload?.reason;
      transferSidRef.current = null;
      setTransferBusy(false);
      // Busy isn't a failure to retry — the target is mid-conversation and the sender must
      // confirm. Keep the picker as-is and surface a "transfer anyway?" prompt.
      if (reason === "target_busy" && payload?.target) {
        setBusyConfirm(payload.target);
        setTransferError("");
        return;
      }
      let text;
      if (reason === "target_offline") text = "That agent just went offline — pick someone else.";
      else if (reason === "closed") text = "This chat already ended.";
      else if (reason === "self") text = "You already own this chat.";
      else text = "You no longer own this chat.";
      setBusyConfirm(null);
      setTransferError(text);
      fetchAgents().then((res) => setDirectory(res?.data?.agents || [])).catch(() => {});
    };
    const onAccepted = (payload) => {
      const s = payload?.session;
      if (!s) return;
      // history is the shopper's whole thread (all sessions) — replace, don't append.
      upsert(s, { messages: historyToMsgs(payload.history), unread: 0 });
      setActiveKey(keyOf(s));
      setOpen(true);
      setReopenBusy(false);
      setAcceptBusy(false);
      // The draft preview (if any) just became a real conversation.
      setDraftCustomer(null);
      setDraftInitiating(false);
    };
    const onClaimFailed = () => {
      loadSessions();
      setDraftInitiating(false);
      setReopenBusy(false);
      setAcceptBusy(false);
    };
    const onClaimed = (payload) => {
      const sess = payload?.session;
      const sid = payload?.session_id || sess?.id;
      const agentId = payload?.agent_id;
      if (!sid) return;
      const mine = !!(meRef.current && agentId != null && agentId === meRef.current.agent_id);
      if (sess) {
        const patch = {};
        if (mine) patch.unread = 0;
        if (mine && payload.history) patch.messages = historyToMsgs(payload.history);
        upsert(sess, patch);
        setReopenBusy(false);
        if (mine) setAcceptBusy(false);
        if (mine) {
          setActiveKey(keyOf(sess));
          setOpen(true);
        }
      }
      setConvos((prev) => {
        const key = convKeyForSession(prev, sid);
        if (!key) return prev;
        const conv = prev[key];
        const viewing = key === activeRef.current || !!(conv.messages && conv.messages.length);
        const sessions = { ...conv.sessions };
        if (sessions[sid]) {
          sessions[sid] = {
            ...sessions[sid],
            assigned_agent_id: agentId ?? sessions[sid].assigned_agent_id,
            status: STATUS.OPEN,
          };
        }
        if (sess && (mine || viewing)) return prev;
        if (!mine && !viewing) {
          delete sessions[sid];
          const next = { ...prev };
          if (Object.keys(sessions).length) next[key] = { ...conv, sessions };
          else delete next[key];
          return next;
        }
        return { ...prev, [key]: { ...conv, sessions } };
      });
    };
    const onMessage = (payload) => {
      const m = payload?.message;
      if (!m) return;
      let msgKey = null;
      let duplicate = false;
      setConvos((prev) => {
        const key = convKeyForSession(prev, m.session_id);
        msgKey = key;
        if (!key) {
          const buf = pendingRef.current[m.session_id] || (pendingRef.current[m.session_id] = []);
          if (!buf.some((x) => x.id === m.id)) buf.push(toMsg(m));
          return prev;
        }
        const conv = prev[key];
        if (conv.messages.some((x) => x.id === m.id)) {
          duplicate = true;
          return prev;
        }
        if (m.sender_type === SENDER.AGENT) {
          const hasOptimistic = conv.messages.some(
            (x) => typeof x.id === "string" && x.id.startsWith("me-") && x.body === m.body
          );
          if (hasOptimistic) {
            const replaced = conv.messages.map((x) =>
              typeof x.id === "string" && x.id.startsWith("me-") && x.body === m.body ? toMsg(m) : x
            );
            return { ...prev, [key]: { ...conv, messages: replaced } };
          }
        }
        const isActive = openRef.current && activeRef.current === key;
        const bumpUnread = !isActive && m.sender_type !== SENDER.AGENT;
        return {
          ...prev,
          [key]: {
            ...conv,
            messages: [...conv.messages, toMsg(m)],
            unread: bumpUnread ? (conv.unread || 0) + 1 : conv.unread || 0,
          },
        };
      });
      if (!duplicate && m.sender_type === SENDER.CUSTOMER && msgKey) {
        const focused = typeof document.hasFocus === "function" ? document.hasFocus() : true;
        playPing();
        if (!focused) {
          const conv = convosRef.current[msgKey];
          const sess = conv?.sessions?.[m.session_id] || (conv && latestOf(conv));
          const who = sess ? displayName(sess) : "Customer";
          flashTitle("💬 New message");
          notifyBrowser("New chat message", `${who}${m.body ? `: ${m.body}` : ""}`);
        }
      }
      loadActiveThread();
      loadSessions();
    };
    const onClosed = (payload) => {
      const sid = payload?.session_id;
      if (!sid) return;
      // Wording mirrors the SYSTEM note the server persists on close, so the live event
      // and a later thread reload read identically. `left_queue` = the customer abandoned
      // a chat no agent had accepted; the status flip to CLOSED also drops the Accept bar.
      const endedBy = payload?.ended_by;
      const leftQueue = !!payload?.left_queue;
      const name = payload?.ended_by_name;
      setConvos((prev) => {
        const key = convKeyForSession(prev, sid);
        if (!key) return prev;
        const conv = prev[key];
        const who = name || displayName(conv.sessions[sid]) || "The customer";
        let text;
        if (leftQueue) text = `${who} left the chat`;
        else if (endedBy === "system") text = "Chat closed due to inactivity";
        else if (endedBy === "visitor") text = `Chat closed by ${name || "the customer"}`;
        else text = `Chat closed by ${name || "the agent"}`;
        return {
          ...prev,
          [key]: {
            ...conv,
            sessions: { ...conv.sessions, [sid]: { ...conv.sessions[sid], status: STATUS.CLOSED } },
            messages: [...conv.messages, { id: `sys-${Date.now()}`, side: "system", body: text }],
          },
        };
      });
      loadSessions();
    };
    const onVisitorUpdate = (v) => {
      if (!v || !v.visitor_id) return;
      setPresence((prev) => ({ ...prev, [v.visitor_id]: v }));
      // Update shoppers list in real-time — only logged-in customers (matches REST).
      if (!v.customer_id) return;
      setShoppers((prev) => {
        if (!prev) return prev;
        const online = prev.online || [];
        const idx = online.findIndex(
          (s) => s.visitor_id === v.visitor_id || (v.customer_id && s.customer_id === v.customer_id)
        );
        if (idx >= 0) {
          const next = [...online];
          const merged = { ...next[idx], ...v };
          if (!merged.name && next[idx].name) merged.name = next[idx].name;
          next[idx] = merged;
          return { ...prev, online: next };
        }
        return { ...prev, online: [...online, v] };
      });
    };
    const onVisitorLeft = (data) => {
      const vid = data?.visitor_id;
      if (!vid) return;
      setPresence((prev) => {
        const next = { ...prev };
        delete next[vid];
        return next;
      });
      setShoppers((prev) => {
        if (!prev) return prev;
        const online = (prev.online || []).filter((s) => s.visitor_id !== vid);
        return { ...prev, online };
      });
    };

    // --- voice call signaling (agent is the caller) ---
    const onCallRequested = (payload) => {
      const sid = payload?.session_id;
      if (!sid) return;
      setCallRequests((prev) => ({ ...prev, [sid]: payload.name || "The shopper" }));
      const focused = typeof document.hasFocus === "function" ? document.hasFocus() : true;
      if (!focused || !openRef.current) {
        playPing();
        notifyBrowser("Call requested", `${payload.name || "A shopper"} asked you to call them`);
        flashTitle("📞 Call requested");
      }
    };
    // Server ack that our invite is live (carries TURN creds + the call id). The shopper is
    // now ringing — we wait for their accept before building the peer / making the offer.
    const onCallRinging = (payload) => {
      const cur = callRef.current;
      if (!cur || cur.sessionId !== payload?.session_id) return;
      callIceServersRef.current = payload.ice_servers || [];
      const next = { ...cur, id: payload.call_id, status: "ringing" };
      callRef.current = next;
      setCall(next);
    };
    const onCallUnavailable = (payload) => {
      const prevName = (callRef.current && callRef.current.peerName) || "";
      if (callPeerRef.current) {
        try {
          callPeerRef.current.close();
        } catch (e) {
          /* noop */
        }
        callPeerRef.current = null;
      }
      setCall({
        id: null,
        sessionId: payload?.session_id,
        peerName: prevName,
        status: "ended",
        error: callUnavailableText(payload?.reason),
      });
    };
    // Shopper accepted → we (the caller) grab the mic, build the offer and send it.
    const onCallAccepted = async (payload) => {
      const cur = callRef.current;
      if (!cur || cur.id !== payload?.call_id) return;
      if (!callSupported()) {
        setCall((c) => (c ? { ...c, status: "ended", error: "Voice calls aren't supported in this browser" } : c));
        clearCall("setup_failed");
        return;
      }
      const peer = createVoicePeer({
        role: "caller",
        iceServers: callIceServersRef.current,
        onIce: (candidate) => sendCallIce(cur.sessionId, cur.id, candidate),
        onRemoteStream: (stream) => {
          if (remoteAudioRef.current) remoteAudioRef.current.srcObject = stream;
        },
        onState: (state) => {
          if (state === "connected") {
            notifyCallConnected(cur.sessionId, cur.id);
            setCall((c) => (c && c.status !== "active" ? { ...c, status: "active" } : c));
            if (!callTimerRef.current) {
              callTimerRef.current = setInterval(() => setCallSeconds((s) => s + 1), 1000);
            }
          } else if (state === "failed" || state === "disconnected") {
            if (callRef.current) clearCall("failed");
          }
        },
      });
      callPeerRef.current = peer;
      const next = { ...cur, status: "connecting" };
      callRef.current = next;
      setCall(next);
      try {
        const offer = await peer.createOffer();
        sendCallOffer(cur.sessionId, cur.id, offer);
      } catch (err) {
        const denied = err && (err.name === "NotAllowedError" || err.name === "SecurityError");
        setCall((c) => (c ? { ...c, status: "ended", error: denied ? "Microphone access blocked" : "Couldn't start the call" } : c));
        clearCall(denied ? "mic_denied" : "failed");
      }
    };
    const onCallAnswer = (payload) => {
      const cur = callRef.current;
      if (!cur || !callPeerRef.current || cur.id !== payload?.call_id) return;
      callPeerRef.current.acceptAnswer(payload.sdp).catch(() => clearCall("failed"));
    };
    const onCallIceIn = (payload) => {
      const cur = callRef.current;
      if (!cur || !callPeerRef.current || cur.id !== payload?.call_id) return;
      callPeerRef.current.addIce(payload.candidate);
    };
    const onCallEnded = (payload) => {
      const cur = callRef.current;
      if (!cur) return;
      if (payload && payload.session_id && cur.sessionId && payload.session_id !== cur.sessionId) return;
      // Server already tore down its side (declined / no-answer / hangup / failure). Clean up
      // locally — the in-thread call note arrives separately as a normal message.
      clearCall();
    };

    sock.on("connect", onConnect);
    sock.on("ecom_agent_ready", onReady);
    sock.on("ecom_chat_incoming", onIncoming);
    sock.on("ecom_chat_accepted", onAccepted);
    sock.on("ecom_chat_claim_failed", onClaimFailed);
    sock.on("ecom_chat_claimed", onClaimed);
    sock.on("ecom_chat_updated", onUpdated);
    sock.on("ecom_chat_transferred_in", onTransferredIn);
    sock.on("ecom_chat_transferred_out", onTransferredOut);
    sock.on("ecom_chat_transfer_failed", onTransferFailed);
    sock.on("ecom_chat_message", onMessage);
    sock.on("ecom_chat_closed", onClosed);
    sock.on("ecom_visitor_update", onVisitorUpdate);
    sock.on("ecom_visitor_left", onVisitorLeft);
    sock.on("ecom_call_requested", onCallRequested);
    sock.on("ecom_call_ringing", onCallRinging);
    sock.on("ecom_call_unavailable", onCallUnavailable);
    sock.on("ecom_call_accepted", onCallAccepted);
    sock.on("ecom_call_answer", onCallAnswer);
    sock.on("ecom_call_ice", onCallIceIn);
    sock.on("ecom_call_ended", onCallEnded);
    // Load immediately on mount (don't wait for socket connect).
    loadSessions();
    loadShoppers();
    if (sock.connected) onConnect();

    // Socket events (ecom_visitor_update/left) provide real-time shoppers updates.
    // Periodic REST is only a safety net for missed events or abandoned cart refreshes.
    const shopperRefresh = setInterval(loadShoppers, PRESENCE_REFRESH_MS);
    const sessionRefresh = setInterval(loadSessions, PRESENCE_REFRESH_MS);

    return () => {
      clearInterval(shopperRefresh);
      clearInterval(sessionRefresh);
      sock.off("connect", onConnect);
      sock.off("ecom_agent_ready", onReady);
      sock.off("ecom_chat_incoming", onIncoming);
      sock.off("ecom_chat_accepted", onAccepted);
      sock.off("ecom_chat_claim_failed", onClaimFailed);
      sock.off("ecom_chat_claimed", onClaimed);
      sock.off("ecom_chat_updated", onUpdated);
      sock.off("ecom_chat_transferred_in", onTransferredIn);
      sock.off("ecom_chat_transferred_out", onTransferredOut);
      sock.off("ecom_chat_transfer_failed", onTransferFailed);
      sock.off("ecom_chat_message", onMessage);
      sock.off("ecom_chat_closed", onClosed);
      sock.off("ecom_visitor_update", onVisitorUpdate);
      sock.off("ecom_visitor_left", onVisitorLeft);
      sock.off("ecom_call_requested", onCallRequested);
      sock.off("ecom_call_ringing", onCallRinging);
      sock.off("ecom_call_unavailable", onCallUnavailable);
      sock.off("ecom_call_accepted", onCallAccepted);
      sock.off("ecom_call_answer", onCallAnswer);
      sock.off("ecom_call_ice", onCallIceIn);
      sock.off("ecom_call_ended", onCallEnded);
      // Drop any live call without a setState (component is unmounting / reconnecting).
      if (callPeerRef.current) {
        try {
          callPeerRef.current.close();
        } catch (e) {
          /* noop */
        }
        callPeerRef.current = null;
      }
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
      }
      // The dock owns the connection for the whole session; tear it down on unmount.
      disconnectAgentSocket();
    };
  }, [authToken]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [convos, activeKey, open]);

  // Debounced customer search via REST API (replaces socket emit).
  useEffect(() => {
    const q = search.trim();
    searchRef.current = q;
    if (q.length < CUSTOMER_SEARCH_MIN_CHARS) {
      setCustomerResults(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      searchCustomersApi(q)
        .then((res) => {
          if (searchRef.current !== q) return;
          const rows = res?.data?.data || res?.data?.customers || [];
          setCustomerResults(
            rows.map((c) => ({
              id: c.id,
              name: c.name || c.business_name,
              email: c.email,
              phone: c.phone_no,
              business_name: c.business_name,
            }))
          );
        })
        .catch(() => {});
    }, CUSTOMER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Shoppers tab: extra load on tab open (polling is global in main useEffect).
  useEffect(() => {
    if (!open || tab !== "shoppers") return undefined;
    fetchShoppers()
      .then((res) => {
        const payload = res?.data;
        if (!payload) return;
        setShoppers({ online: payload.online || [], abandoned: payload.abandoned || [] });
      })
      .catch(() => {});
    return undefined;
  }, [open, tab]);

  // If the active conversation was folded into another (shopper logged in / matched by
  // phone), follow the alias chain to the surviving thread instead of going blank.
  useEffect(() => {
    if (!activeKey || convos[activeKey]) return;
    let k = activeKey;
    const seen = new Set();
    while (aliasRef.current[k] && !seen.has(k)) {
      seen.add(k);
      k = aliasRef.current[k];
    }
    setActiveKey(convos[k] ? k : null);
  }, [convos, activeKey]);

  // Who's on the storefront right now: device ids + verified customer ids on the board.
  const online = useMemo(() => {
    const visitors = new Set();
    const customers = new Set();
    Object.values(presence).forEach((v) => {
      if (v.visitor_id) visitors.add(v.visitor_id);
      if (v.customer_id != null) customers.add(String(v.customer_id));
    });
    return { visitors, customers };
  }, [presence]);
  // A shopper is online when any of their sessions maps to a live presence record —
  // by device (visitor_id) or by login (customer_id follows them across devices).
  const isShopperOnline = (conv) =>
    Object.values((conv && conv.sessions) || {}).some(
      (s) =>
        (s.visitor_id && online.visitors.has(s.visitor_id)) ||
        (s.customer_id != null && online.customers.has(String(s.customer_id)))
    );

  const list = useMemo(
    () =>
      Object.entries(convos)
        .map(([key, conv]) => ({ key, conv, session: threadLatestSession(conv), identity: identityOf(conv) }))
        .filter((x) => x.session)
        .sort((a, b) =>
          (b.session.last_message_at || b.session.created_at || "").localeCompare(
            a.session.last_message_at || a.session.created_at || ""
          )
        ),
    [convos]
  );
  const searching = !!search.trim();
  const visibleList = searching ? list.filter((x) => convoMatchesSearch(x.conv, search)) : list;
  const totalUnread = list.reduce((n, x) => n + (x.conv.unread || 0), 0);
  // Plain-language hover summary for the floating bubble (e.g. "2 customers are online · 4 chats unread").
  const onlineCount = online.customers.size;
  const fabTitle =
    `${onlineCount} customer${onlineCount === 1 ? " is" : "s are"} online` +
    ` · ${totalUnread} chat${totalUnread === 1 ? "" : "s"} unread`;
  const active = activeKey ? convos[activeKey] : null;
  const activeSession = active ? threadLatestSession(active) : null;
  const activeIdentity = active ? identityOf(active) : null;
  const showInfo = !!(activeSession && infoOpen && isCustomer(activeIdentity));
  // While the info panel is showing, keep its cart/page live: re-pull the context on the
  // same cadence as the presence board so the sidebar matches the Shoppers tab numbers
  // (the old fetch-once-per-conversation cache showed the cart as of first open, forever).
  const infoSessionId = showInfo && activeIdentity ? activeIdentity.id : null;
  useEffect(() => {
    if (!infoSessionId) return undefined;
    const loadCtx = () => {
      fetchContext(infoSessionId)
        .then((res) => {
          if (!res?.data) return;
          setConvos((prev) => {
            const key = convKeyForSession(prev, infoSessionId);
            if (!key) return prev;
            return { ...prev, [key]: { ...prev[key], ctx: res.data } };
          });
        })
        .catch(() => {});
    };
    loadCtx();
  }, [infoSessionId]);
  const activeClosed = !!(activeSession && activeSession.status === STATUS.CLOSED);
  // A fresh session merged into this thread but isn't ours yet → show the Accept bar.
  const needsAccept = !!(
    activeSession &&
    !activeClosed &&
    (activeSession.status === STATUS.QUEUED || activeSession.status === STATUS.OFFLINE || !activeSession.assigned_agent_id)
  );
  // Claimed by a colleague: composer off, "handled by" bar on. Unknown self-identity
  // (no ecom_agent_ready yet) degrades to the old behavior — composer shows and the
  // server's ownership check still protects the session.
  const myAgentId = me && me.agent_id;
  const ownedByOther = !!(
    activeSession &&
    !activeClosed &&
    myAgentId &&
    activeSession.assigned_agent_id &&
    activeSession.assigned_agent_id !== myAgentId
  );
  // The agent can dial out only on a chat they own, when the shopper is on the site now.
  const canCallActive = !!(activeSession && !activeClosed && !needsAccept && !ownedByOther && isShopperOnline(active));
  // A "requested a call" chip the shopper raised on any session in the active thread.
  let activeCallRequest = null;
  if (active) {
    for (const sid of Object.keys(active.sessions || {})) {
      if (callRequests[sid]) {
        activeCallRequest = { sid, name: callRequests[sid] };
        break;
      }
    }
  }

  const openConversation = ({ key, conv, session }) => {
    setDraftCustomer(null);
    setDraftInitiating(false);
    // Opening a conversation is READ-ONLY — clicking a list row never claims the chat.
    // An unaccepted (QUEUED/OFFLINE/unassigned) chat shows its history plus the "Accept
    // chat" bar (needsAccept); the agent must click Accept to claim it (ecom_chat_accept)
    // and drop into the session. Load the read-only cross-session history (nothing is
    // claimed — same `ecom_agent_get_thread` path used for recently-closed chats) only
    // when we don't already have the messages locally, so a live thread is never clobbered.
    if (!conv.messages.length) {
      fetchThread(session.id)
        .then((res) => {
          const { session_id: sid, history } = res?.data || {};
          if (!sid) return;
          setConvos((prev) => {
            const k = convKeyForSession(prev, sid);
            if (!k || prev[k].messages.length) return prev;
            return { ...prev, [k]: { ...prev[k], messages: historyToMsgs(history) } };
          });
        })
        .catch(() => {});
    }
    setActiveKey(key); // show what we already have while history loads
    // Pre-load the profile/orders/cart sidebar (customers only) — refreshed on every open
    // so a cached panel never shows a stale cart. Resolve the customer through the thread's
    // identity session — the newest session may be a signed-out one (no customer_id), but
    // the shopper is still a known customer.
    const idSess = identityOf(conv) || session;
    if (isCustomer(idSess)) {
      fetchContext(idSess.id)
        .then((res) => {
          if (!res?.data) return;
          setConvos((prev) => {
            const k = convKeyForSession(prev, idSess.id);
            if (!k) return prev;
            return { ...prev, [k]: { ...prev[k], ctx: res.data } };
          });
        })
        .catch(() => {});
    }
    setConvos((prev) => (prev[key] ? { ...prev, [key]: { ...prev[key], unread: 0 } } : prev));
  };

  // Re-ping a shopper whose conversation has ended: a customer can always be reached
  // (offline → queued + notified on their next visit); a guest only while they're still
  // browsing (no durable identity to notify once they're gone). The server starts a fresh
  // session in the same thread and replies with ecom_chat_accepted, so the composer
  // comes back and the agent can type straight away.
  const canReopenActive = !!(
    activeSession && ((activeIdentity && activeIdentity.customer_id) || isShopperOnline(active))
  );
  const reopenActiveChat = () => {
    if (!activeSession || reopenBusy) return;
    setReopenBusy(true);
    // Reach the shopper through their durable identity: a thread whose newest session was
    // signed-out still belongs to a known customer who can be queued/notified offline.
    if (activeIdentity && activeIdentity.customer_id) initiateChatWithCustomer(activeIdentity.customer_id);
    else initiateChat(activeSession.visitor_id);
  };

  // --- transfer / release (owner-only actions on the active session) ---
  const openTransferModal = () => {
    setTransferOpen(true);
    setDirectory(null); // null = loading state while the server replies
    setTransferTarget(null);
    setTransferNote("");
    setTransferError("");
    setTransferBusy(false);
    setBusyConfirm(null);
    fetchAgents().then((res) => setDirectory(res?.data?.agents || [])).catch(() => setDirectory([]));
  };
  // Picking a (different) agent clears a pending busy warning so it can't carry over.
  const selectTransferTarget = (agentId) => {
    setTransferTarget(agentId);
    setBusyConfirm((prev) => (prev && prev.agent_id === agentId ? prev : null));
  };
  // `force` is set by the "Transfer anyway" button after a busy warning.
  const confirmTransfer = (force = false) => {
    if (!activeSession || !transferTarget) return;
    transferSidRef.current = activeSession.id;
    setTransferBusy(true);
    setTransferError("");
    transferChat(activeSession.id, transferTarget, transferNote.trim() || undefined, force);
  };
  // True when the pending busy warning is for the agent currently selected.
  const targetBusyWarned = !!(busyConfirm && busyConfirm.agent_id === transferTarget);
  const releaseToQueue = () => {
    if (!activeSession) return;
    transferSidRef.current = activeSession.id;
    setTransferBusy(true);
    setTransferError("");
    releaseChat(activeSession.id);
  };
  const dismissTransferNote = () =>
    setConvos((prev) =>
      prev[activeKey] ? { ...prev, [activeKey]: { ...prev[activeKey], transferNote: undefined } } : prev
    );

  // Stop the tab-title flash once everything has been read (focus also stops it).
  useEffect(() => {
    if (totalUnread === 0) stopTitleFlash();
    window.__kappEcomChatUnread = totalUnread;
    window.dispatchEvent(new CustomEvent("kapp:ecomchat-unread", { detail: { count: totalUnread } }));
  }, [totalUnread]);

  useEffect(() => {
    window.__kappEcomChatOpen = open;
    window.dispatchEvent(new CustomEvent("kapp:ecomchat-state", { detail: { open } }));
  }, [open]);

  useEffect(() => {
    window.__kappEcomChatOnline = onlineCount;
    window.dispatchEvent(new CustomEvent("kapp:ecomchat-online", { detail: { count: onlineCount } }));
  }, [onlineCount]);

  useEffect(() => {
    const onHeaderToggle = (e) => {
      const next = e?.detail?.open;
      if (typeof next === "boolean") setOpen(next);
      else setOpen((o) => !o);
    };
    window.addEventListener("kapp:toggle-ecomchat", onHeaderToggle);
    return () => window.removeEventListener("kapp:toggle-ecomchat", onHeaderToggle);
  }, []);
  // Live online state for the draft preview (server flag at search time + live presence).
  const draftOnline = !!(
    draftCustomer && (draftCustomer.online || online.customers.has(String(draftCustomer.id)))
  );

  // Agent picked a customer from the search results. Never drops straight into chat mode:
  // an existing conversation opens as-is (live → composer/Accept; closed → read-only thread
  // with the "Reopen chat" button); a first-time customer gets a preview pane with an
  // explicit "Initiate chat now" action. Either way an offline customer's messages queue
  // and they're notified on their next storefront visit.
  const selectCustomerResult = (customer) => {
    setSearch("");
    setCustomerResults(null);
    const existingKey = `c:${customer.id}`;
    const existing = convos[existingKey];
    const latest = existing && latestOf(existing);
    if (latest) {
      openConversation({ key: existingKey, conv: existing, session: latest });
      return;
    }
    setDraftCustomer(customer);
    setDraftInitiating(false);
    setActiveKey(null);
  };

  // Shoppers-tab row clicked: route back to the Chats tab through the same explicit-action
  // flow as a search result (existing thread opens / first-time gets "Initiate chat now").
  const pingShopper = (row, isOnline) => {
    setTab("chats");
    selectCustomerResult({
      id: row.customer_id,
      name: row.name,
      business_name: row.business_name,
      email: row.email,
      online: !!isOnline,
    });
  };

  const viewShopperInfo = (e, row) => {
    e.stopPropagation();
    setShopperInfo({ customer_id: row.customer_id, name: row.name, ctx: undefined });
    fetchCustomerContext(row.customer_id)
      .then((res) => {
        setShopperInfo((prev) =>
          prev && prev.customer_id === row.customer_id
            ? { ...prev, ctx: res?.data || null }
            : prev
        );
      })
      .catch(() => {
        setShopperInfo((prev) =>
          prev && prev.customer_id === row.customer_id ? { ...prev, ctx: null } : prev
        );
      });
  };

  // Append an optimistic outgoing bubble to the active conversation.
  const pushLocal = (msg) =>
    setConvos((prev) => {
      const conv = prev[activeKey];
      if (!conv) return prev;
      return {
        ...prev,
        [activeKey]: {
          ...conv,
          messages: [
            ...conv.messages,
            { id: `me-${Date.now()}`, side: "me", ts: new Date().toISOString(), ...msg },
          ],
        },
      };
    });

  const handleSend = (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !activeSession) return;
    pushLocal({ body, message_type: MESSAGE_TYPE.TEXT });
    sendAgentMessage(activeSession.id, { body, message_type: MESSAGE_TYPE.TEXT });
    setDraft("");
    setEmojiOpen(false);
  };

  const insertEmoji = (emoji) => setDraft((d) => d + emoji);

  // Upload a file/blob to S3, optimistically render it, then emit the socket message.
  const sendMedia = async (file, name) => {
    if (!activeSession) return;
    if (file.size > MAX_FILE_BYTES) {
      setMediaError("File is too large (max 10 MB).");
      return;
    }
    const mime = file.type || "application/octet-stream";
    if (!isAllowedMime(mime)) {
      setMediaError("That file type isn't supported.");
      return;
    }
    const message_type = typeForMime(mime);
    setMediaError("");
    setUploading(true);
    try {
      const attachment = await uploadChatFile(file, name);
      pushLocal({ message_type, attachment });
      sendAgentMessage(activeSession.id, { message_type, attachment });
    } catch (err) {
      setMediaError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handlePickFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-picking the same file
    if (file) sendMedia(file);
  };

  // --- voice call actions (agent is the caller) ---
  // Ring the shopper of the active session. The server acks with `ecom_call_ringing`
  // (creds + call_id); we build the peer + offer only once the shopper accepts.
  const startCallActive = () => {
    if (!activeSession || call) return;
    const peerName = displayName(activeIdentity || activeSession);
    if (!callSupported()) {
      setCall({ id: null, sessionId: activeSession.id, peerName, status: "ended", error: "Voice calls aren't supported in this browser" });
      return;
    }
    // Clear any "requested a call" chip across this thread's sessions.
    setCallRequests((prev) => {
      const next = { ...prev };
      Object.keys((active && active.sessions) || {}).forEach((sid) => delete next[sid]);
      return next;
    });
    const next = { id: null, sessionId: activeSession.id, peerName, status: "ringing", error: null };
    callRef.current = next;
    setCall(next);
    startCall(activeSession.id);
  };
  const hangupCall = () => clearCall("hangup");
  const toggleCallMute = () =>
    setCallMuted((m) => {
      const nextMuted = !m;
      if (callPeerRef.current) callPeerRef.current.setMuted(nextMuted);
      return nextMuted;
    });
  const dismissCallRequest = (sid) =>
    setCallRequests((prev) => {
      const next = { ...prev };
      delete next[sid];
      return next;
    });
  // Non-blocking call UI: a slim strip (Mute / End / status) reused both as an in-dock bar
  // under the header AND as a floating pill when the dock is minimized — so the agent can keep
  // chatting, switch conversations and use every other control while the call runs.
  const renderCallStrip = () =>
    call ? (
      <>
        <span style={styles.callBarIcon} aria-hidden>
          📞
        </span>
        <div style={styles.callBarText}>
          <span style={styles.callBarName}>{call.peerName || "Shopper"}</span>
          <span style={styles.callBarStatus}>
            {call.status === "ringing" && "Ringing…"}
            {call.status === "connecting" && "Connecting…"}
            {call.status === "active" && `On call · ${fmtCallTime(callSeconds)}`}
            {call.status === "ended" && (call.error || "Call ended")}
          </span>
        </div>
        <div style={styles.callBarActions}>
          {call.status === "active" && (
            <button
              type="button"
              style={{ ...styles.callBarMute, ...(callMuted ? styles.callMuteOn : {}) }}
              onClick={toggleCallMute}
              title={callMuted ? "Unmute" : "Mute"}
              aria-label={callMuted ? "Unmute" : "Mute"}
            >
              {callMuted ? "🔇" : "🎙"}
            </button>
          )}
          {(call.status === "ringing" || call.status === "connecting" || call.status === "active") && (
            <button type="button" style={styles.callBarEnd} onClick={hangupCall}>
              {call.status === "active" ? "End" : "Cancel"}
            </button>
          )}
          {call.status === "ended" && (
            <button type="button" style={styles.callBarDismiss} onClick={() => clearCall()}>
              Dismiss
            </button>
          )}
        </div>
      </>
    ) : null;

  return (
    <div style={styles.root}>
      {open && (
        <div style={{ ...styles.panel, ...(maximized ? styles.panelMax : { width: (showInfo || (draftCustomer && shopperInfo && shopperInfo.customer_id === draftCustomer.id)) ? 820 : 540 }) }}>
          <div style={styles.header}>
            <span>Ecom chats</span>
            <div style={styles.headerActions}>
              <button
                style={styles.close}
                onClick={() => setMaximized((m) => !m)}
                aria-label={maximized ? "Restore size" : "Maximize"}
                title={maximized ? "Restore size" : "Maximize"}
              >
                {maximized ? "❐" : "⤢"}
              </button>
              <button style={styles.close} onClick={() => setOpen(false)} aria-label="Minimize" title="Minimize">
                –
              </button>
            </div>
          </div>
          {/* Non-blocking call bar — the dock below stays fully usable during a call. */}
          {call && <div style={styles.callBar}>{renderCallStrip()}</div>}
          {tab === "chats" && (
          <div style={styles.cols}>
            <div style={styles.listCol}>
              <div style={styles.searchBox}>
                <input
                  style={styles.searchInput}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search or start a chat…"
                  aria-label="Search conversations or customers"
                />
              </div>
              {!searching && list.length === 0 && <div style={styles.empty}>No conversations</div>}
              {searching && visibleList.length === 0 && (
                <div style={styles.searchEmpty}>No matching conversations</div>
              )}
              {visibleList.map((x) => (
                <div
                  key={x.key}
                  style={{ ...styles.listItem, ...(activeKey === x.key ? styles.listItemActive : {}) }}
                  onClick={() => openConversation(x)}
                >
                  <div style={styles.listRow}>
                    <Avatar session={x.identity || x.session} size={32} online={isShopperOnline(x.conv)} />
                    <div style={styles.listBody}>
                      <div style={styles.listTop}>
                        <NameBlock session={x.identity || x.session} style={styles.listName} />
                        {x.conv.unread > 0 && <span style={styles.dot}>{x.conv.unread}</span>}
                      </div>
                      <div style={styles.listChips}>
                        <IdentityChip session={x.identity || x.session} />
                        <DeptTag session={x.session} />
                      </div>
                      <div style={styles.listPreview}>
                        {x.session.status === STATUS.QUEUED && "🟡 Waiting · "}
                        {x.session.status === STATUS.OFFLINE && "🌙 Offline · "}
                        {x.session.status === STATUS.CLOSED && "✔ Closed · "}
                        {x.session.preview || (x.conv.messages[x.conv.messages.length - 1] || {}).body || "…"}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {customerResults && (
                <>
                  <div style={styles.searchSection}>Start a new chat</div>
                  {customerResults.length === 0 && (
                    <div style={styles.searchEmpty}>No matching customers</div>
                  )}
                  {customerResults.map((c) => {
                    const isOnline = c.online || online.customers.has(String(c.id));
                    return (
                      <div
                        key={`cust-${c.id}`}
                        style={styles.listItem}
                        onClick={() => selectCustomerResult(c)}
                        title={isOnline ? "Start chat — browsing now" : "Start chat — they'll be notified on their next visit"}
                      >
                        <div style={styles.listRow}>
                          <Avatar
                            session={customerAsSession(c.id, c.name, c.business_name)}
                            size={28}
                            online={isOnline}
                          />
                          <div style={styles.listBody}>
                            <div style={styles.listTop}>
                              <NameBlock session={customerAsSession(c.id, c.name, c.business_name)} style={styles.listName} />
                              <span style={styles.startChatHint}>Chat</span>
                            </div>
                            <div style={styles.searchResultSub}>
                              {c.email || c.phone || (isOnline ? "Online now" : "Offline")}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
            <div style={styles.threadCol}>
              {!active && !draftCustomer && <div style={styles.empty}>Select a conversation</div>}
              {!active && draftCustomer && (
                <>
                  <div style={styles.threadHead}>
                    <div style={styles.threadWho}>
                      <Avatar
                        session={customerAsSession(draftCustomer.id, draftCustomer.name, draftCustomer.business_name)}
                        size={28}
                        online={draftOnline}
                      />
                      <div style={styles.threadWhoText}>
                        <NameBlock
                          session={customerAsSession(draftCustomer.id, draftCustomer.name, draftCustomer.business_name)}
                          style={styles.threadName}
                        />
                        <div style={styles.listChips}>
                          <IdentityChip
                            session={{
                              customer_id: draftCustomer.id,
                              customer_email: draftCustomer.email,
                              is_customer: true,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <button
                      style={{ ...styles.infoBtn, ...(shopperInfo && shopperInfo.customer_id === draftCustomer.id ? styles.infoBtnActive : {}) }}
                      onClick={(e) => {
                        if (shopperInfo && shopperInfo.customer_id === draftCustomer.id) {
                          setShopperInfo(null);
                        } else {
                          viewShopperInfo(e, { customer_id: draftCustomer.id, name: draftCustomer.name });
                        }
                      }}
                      aria-label="View Cart"
                      title="View Cart"
                    >
                      View Cart
                    </button>
                  </div>
                  <div style={styles.thread}>
                    <div style={styles.draftHint}>
                      <div style={styles.draftHintTitle}>No conversation with this customer yet.</div>
                      {draftOnline
                        ? "They're browsing the storefront right now — the chat will open on their screen."
                        : "They're offline right now — your messages will be delivered and they'll see a notification on their next visit."}
                    </div>
                  </div>
                  <div style={styles.acceptBar}>
                    <button
                      style={{ ...styles.initiateBtn, ...(draftInitiating ? styles.initiateBtnBusy : {}) }}
                      disabled={draftInitiating}
                      onClick={() => {
                        setDraftInitiating(true);
                        initiateChatWithCustomer(draftCustomer.id);
                      }}
                    >
                      {draftInitiating ? "Starting…" : "Initiate chat now"}
                    </button>
                  </div>
                </>
              )}
              {active && activeSession && (
                <>
                  <div style={styles.threadHead}>
                    <div style={styles.threadWho}>
                      <Avatar session={activeIdentity || activeSession} size={28} online={isShopperOnline(active)} />
                      <div style={styles.threadWhoText}>
                        <NameBlock session={activeIdentity || activeSession} style={styles.threadName} />
                        <div style={styles.listChips}>
                          <IdentityChip session={activeIdentity || activeSession} />
                          <DeptTag session={activeSession} />
                        </div>
                      </div>
                    </div>
                    <div style={styles.threadActions}>
                      {isCustomer(activeIdentity) && (
                        <button
                          style={{ ...styles.infoBtn, ...(infoOpen ? styles.infoBtnActive : {}) }}
                          onClick={() => setInfoOpen((o) => !o)}
                          aria-label="View Cart"
                          title="View Cart"
                        >
                          View Cart
                        </button>
                      )}
                      {!activeClosed && !needsAccept && !ownedByOther && (
                        <button
                          style={{ ...styles.endBtn, ...(canCallActive ? styles.callBtn : {}) }}
                          onClick={startCallActive}
                          disabled={!canCallActive || !!call}
                          aria-label="Call shopper"
                          title={
                            canCallActive
                              ? "Call the shopper"
                              : "Shopper is offline — voice calls work only while they're on the site"
                          }
                        >
                          📞 Call
                        </button>
                      )}
                      {!activeClosed && !needsAccept && !ownedByOther && (
                        <button
                          style={styles.endBtn}
                          onClick={openTransferModal}
                          aria-label="Transfer chat"
                          title="Transfer to another agent or send back to the queue"
                        >
                          ⇄ Transfer
                        </button>
                      )}
                      {!activeClosed && !needsAccept && !ownedByOther && (
                        <button style={styles.endBtn} onClick={() => closeChat(activeSession.id)}>
                          End
                        </button>
                      )}
                    </div>
                  </div>
                  {active.transferNote && (
                    <div style={styles.noteBanner}>
                      <span>
                        📝 Note from {active.transferNote.from || "your colleague"}: {active.transferNote.text}
                      </span>
                      <button style={styles.noteDismiss} onClick={dismissTransferNote} aria-label="Dismiss note">
                        ×
                      </button>
                    </div>
                  )}
                  {activeCallRequest && (
                    <div style={styles.callReqBanner}>
                      <span>📞 {activeCallRequest.name} requested a call</span>
                      <span style={styles.callReqActions}>
                        {canCallActive && !call && (
                          <button style={styles.callReqBtn} onClick={startCallActive}>
                            Call back
                          </button>
                        )}
                        <button
                          style={styles.noteDismiss}
                          onClick={() => dismissCallRequest(activeCallRequest.sid)}
                          aria-label="Dismiss call request"
                        >
                          ×
                        </button>
                      </span>
                    </div>
                  )}
                  <div style={styles.thread}>
                    {active.messages.map((m, i) => {
                      const prev = active.messages[i - 1];
                      const chip = dateChipBetween(prev, m);
                      const showSessionDivider = prev && m.session_id && prev.session_id && m.session_id !== prev.session_id;
                      const time = m.side !== "system" && m.ts ? fmtTime(m.ts) : null;
                      return (
                        <React.Fragment key={m.id}>
                          {chip && <div style={styles.dateChip}>{chip}</div>}
                          {showSessionDivider && <div style={bubbleStyle("system")}>New chat started</div>}
                          <div style={bubbleStyle(m.side)}>
                            <ChatAttachment msg={m} />
                            {time && (
                              <div style={{ ...styles.msgTime, ...(m.side === "me" ? styles.msgTimeMe : {}) }}>
                                {time}
                              </div>
                            )}
                          </div>
                        </React.Fragment>
                      );
                    })}
                    <div ref={endRef} />
                  </div>
                  {mediaError && !activeClosed && !needsAccept && <div style={styles.mediaError}>{mediaError}</div>}
                  {activeClosed ? (
                    <div style={styles.endedNote}>
                      <div>This chat has ended.</div>
                      {canReopenActive && (
                        <button
                          style={{ ...styles.reopenBtn, ...(reopenBusy ? { opacity: 0.7, pointerEvents: "none" } : {}) }}
                          disabled={reopenBusy}
                          onClick={reopenActiveChat}
                        >
                          {reopenBusy ? "Reopening…" : "Reopen chat"}
                        </button>
                      )}
                    </div>
                  ) : needsAccept ? (
                    <div style={styles.acceptBar}>
                      <button
                        style={{ ...styles.acceptBtn, ...(acceptBusy ? { opacity: 0.7, pointerEvents: "none" } : {}) }}
                        disabled={acceptBusy}
                        onClick={() => {
                          if (acceptBusy || !activeSession?.id) return;
                          setAcceptBusy(true);
                          acceptChat(activeSession.id);
                        }}
                      >
                        {acceptBusy ? "Accepting…" : "Accept chat"}
                      </button>
                    </div>
                  ) : ownedByOther ? (
                    <div style={styles.handledBar}>
                      👤 {activeSession.assigned_agent_name || "Another agent"} is handling this chat
                    </div>
                  ) : (
                  <form style={styles.composer} onSubmit={handleSend}>
                    {emojiOpen && <ChatEmojiPicker onPick={insertEmoji} onClose={() => setEmojiOpen(false)} />}
                    <input
                      style={styles.input}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder={uploading ? "Uploading…" : "Enter your message…"}
                      disabled={uploading}
                    />
                    <input
                      ref={fileRef}
                      type="file"
                      accept={FILE_ACCEPT}
                      style={{ display: "none" }}
                      onChange={handlePickFile}
                    />
                    <div style={styles.toolbar}>
                      <div style={styles.tools}>
                        <button
                          type="button"
                          style={styles.iconBtn}
                          onClick={() => setEmojiOpen((o) => !o)}
                          aria-label="Emoji"
                          title="Emoji"
                        >
                          😊
                        </button>
                        <button
                          type="button"
                          style={styles.iconBtn}
                          onClick={() => fileRef.current && fileRef.current.click()}
                          disabled={uploading}
                          aria-label="Attach file"
                          title="Attach file"
                        >
                          📎
                        </button>
                      </div>
                      <button
                        type="submit"
                        style={styles.send}
                        disabled={uploading}
                        aria-label="Send"
                        title="Send"
                      >
                        ➤
                      </button>
                    </div>
                  </form>
                  )}
                </>
              )}
            </div>
            {showInfo && (
              <CustomerInfoPanel session={activeIdentity || activeSession} ctx={active.ctx} expanded={maximized} currencySymbol={currencySymbol} />
            )}
            {!active && draftCustomer && shopperInfo && shopperInfo.customer_id === draftCustomer.id && (
              <CustomerInfoPanel
                session={customerAsSession(draftCustomer.id, draftCustomer.name, draftCustomer.business_name)}
                ctx={shopperInfo.ctx}
                expanded={maximized}
                currencySymbol={currencySymbol}
              />
            )}
          </div>
          )}
          {tab === "shoppers" && (
            <div style={styles.shoppersCol}>
              <div style={styles.searchSection}>
                Online now{shoppers ? ` (${shoppers.online.length})` : ""}
              </div>
              {!shoppers && <div style={styles.searchEmpty}>Loading…</div>}
              {shoppers && shoppers.online.length === 0 && (
                <div style={styles.searchEmpty}>No customers online right now</div>
              )}
              {shoppers &&
                shoppers.online.map((s) => (
                  <div
                    key={`on-${s.customer_id}`}
                    style={styles.listItem}
                    onClick={() => pingShopper(s, true)}
                    title="Open chat — browsing now"
                  >
                    <div style={styles.listRow}>
                      <Avatar
                        session={customerAsSession(s.customer_id, s.name, s.business_name)}
                        size={32}
                        online
                      />
                      <div style={styles.listBody}>
                        <div style={styles.listTop}>
                          <NameBlock session={customerAsSession(s.customer_id, s.name, s.business_name)} style={styles.listName} />
                          <span style={styles.startChatHint}>Chat</span>
                        </div>
                        {s.cart_count > 0 && (
                          <div style={styles.cartLine}>
                            🛒 {s.cart_count} item{s.cart_count === 1 ? "" : "s"}
                            {s.cart_value ? ` · ${money(s.cart_value, currencySymbol)}` : ""}
                          </div>
                        )}
                        {cartItemsPreview(s) && (
                          <div style={styles.searchResultSub}>{cartItemsPreview(s)}</div>
                        )}
                        {(s.page_title || s.page) && (
                          <div style={styles.searchResultSub}>🧭 {s.page_title || s.page}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

              <div style={styles.searchSection}>
                Abandoned carts{shoppers ? ` (${shoppers.abandoned.length})` : ""}
              </div>
              {shoppers && shoppers.abandoned.length === 0 && (
                <div style={styles.searchEmpty}>No abandoned carts</div>
              )}
              {shoppers &&
                shoppers.abandoned.map((s) => (
                  <div
                    key={`ab-${s.customer_id}`}
                    style={styles.listItem}
                    onClick={() => pingShopper(s, false)}
                    title="Ping about their cart — they'll be notified on their next visit"
                  >
                    <div style={styles.listRow}>
                      <Avatar
                        session={customerAsSession(s.customer_id, s.name, s.business_name)}
                        size={32}
                        online={false}
                      />
                      <div style={styles.listBody}>
                        <div style={styles.listTop}>
                          <NameBlock session={customerAsSession(s.customer_id, s.name, s.business_name)} style={styles.listName} />
                          <span style={styles.startChatHint}>Ping</span>
                        </div>
                        <div style={styles.cartLine}>
                          🛒 {s.item_count} item{s.item_count === 1 ? "" : "s"}
                          {timeAgo(s.last_activity) ? ` · left ${timeAgo(s.last_activity)}` : ""}
                        </div>
                        {cartItemsPreview(s) && (
                          <div style={styles.searchResultSub}>{cartItemsPreview(s)}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
          {transferOpen && activeSession && (
            <div style={styles.modalOverlay}>
              <div style={styles.modal}>
                <div style={styles.modalTitle}>Transfer chat</div>
                <div style={styles.modalSub}>
                  Hand {displayName(activeIdentity || activeSession)} to an online teammate, or send the
                  chat back to the shared queue for anyone to pick up.
                </div>
                <div style={styles.modalList}>
                  {directory === null && <div style={styles.searchEmpty}>Loading agents…</div>}
                  {directory && directory.length === 0 && (
                    <div style={styles.searchEmpty}>No other agents are online right now.</div>
                  )}
                  {(directory || []).map((a) => (
                    <div
                      key={a.agent_id}
                      style={{
                        ...styles.agentRow,
                        ...(transferTarget === a.agent_id ? styles.agentRowActive : {}),
                      }}
                      onClick={() => selectTransferTarget(a.agent_id)}
                    >
                      <span style={{ ...styles.agentDot, ...(a.busy ? styles.agentDotBusy : {}) }} />
                      <span style={styles.agentName}>{a.name || `Agent #${a.agent_id}`}</span>
                      {a.busy && <span style={styles.busyPill}>Busy</span>}
                      <span style={styles.agentLoad}>
                        {a.active_chats
                          ? `${a.active_chats} active chat${a.active_chats === 1 ? "" : "s"}`
                          : "Free"}
                      </span>
                    </div>
                  ))}
                </div>
                <input
                  style={styles.noteInput}
                  value={transferNote}
                  onChange={(e) => setTransferNote(e.target.value)}
                  placeholder="Private note for the receiving agent (optional)"
                  aria-label="Transfer note"
                />
                {transferError && <div style={styles.mediaError}>{transferError}</div>}
                {targetBusyWarned && (
                  <div style={styles.busyWarning}>
                    ⚠️ {busyConfirm.name || "That agent"} is in a live conversation right now.
                    Transfer anyway?
                  </div>
                )}
                <div style={styles.modalActions}>
                  <button
                    style={styles.modalGhostBtn}
                    onClick={() => setTransferOpen(false)}
                    disabled={transferBusy}
                  >
                    Cancel
                  </button>
                  <button style={styles.modalGhostBtn} onClick={releaseToQueue} disabled={transferBusy}>
                    Send back to queue
                  </button>
                  <button
                    style={{
                      ...styles.initiateBtn,
                      ...(targetBusyWarned ? styles.transferAnywayBtn : {}),
                      ...(!transferTarget || transferBusy ? styles.initiateBtnBusy : {}),
                    }}
                    onClick={() => confirmTransfer(targetBusyWarned)}
                    disabled={!transferTarget || transferBusy}
                  >
                    {transferBusy ? "Working…" : targetBusyWarned ? "Transfer anyway" : "Transfer"}
                  </button>
                </div>
              </div>
            </div>
          )}
          <div style={styles.tabBar}>
            <button
              style={{ ...styles.tabBtn, ...(tab === "chats" ? styles.tabBtnActive : {}) }}
              onClick={() => setTab("chats")}
            >
              💬 Chats{list.length > 0 ? ` (${list.length})` : ""}
            </button>
            <button
              style={{ ...styles.tabBtn, ...(tab === "shoppers" ? styles.tabBtnActive : {}) }}
              onClick={() => setTab("shoppers")}
            >
              🛒 Shoppers{online.customers.size > 0 ? ` (${online.customers.size})` : ""}
            </button>
          </div>
        </div>
      )}
      {/* When the dock is minimized, the call stays controllable via a floating pill. */}
      {!open && call && <div style={styles.callBarFloating}>{renderCallStrip()}</div>}
      {/* Always mounted so the shopper's audio keeps playing across re-renders. */}
      <audio ref={remoteAudioRef} autoPlay style={{ display: "none" }} />
    </div>
  );
}

// Colored initials circle for a logged-in customer; a dashed grey anonymous mark for a guest.
// `online` drives the presence dot: green = shopper is on the storefront, orange = gone.
function Avatar({ session, size = 32, online = false }) {
  const customer = isCustomer(session);
  const base = {
    width: size,
    height: size,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: Math.round(size * 0.4),
    fontWeight: 600,
  };
  const dotSize = Math.max(10, Math.round(size * 0.32));
  return (
    <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      {customer ? (
        <div style={{ ...base, background: "#dcfce7", color: "#15803d" }}>{initialsOf(session)}</div>
      ) : (
        <div
          style={{ ...base, background: "#f3f4f6", color: "#9ca3af", border: "1px dashed #cbd5e1" }}
          title="Anonymous visitor"
        >
          <span role="img" aria-label="visitor">👤</span>
        </div>
      )}
      <span
        title={online ? "Online" : "Offline"}
        aria-label={online ? "Online" : "Offline"}
        style={{
          position: "absolute",
          bottom: -1,
          right: -1,
          width: dotSize,
          height: dotSize,
          borderRadius: "50%",
          background: online ? "#22c55e" : "#f59e0b",
          border: "2px solid #fff",
          boxSizing: "border-box",
        }}
      />
    </span>
  );
}

// The core differentiation signal: a trust badge — "Customer" (verified login) vs "Visitor".
function IdentityChip({ session }) {
  if (isCustomer(session)) {
    const email = displayEmail(session);
    return (
      <span style={styles.chipRow}>
        <span style={{ ...styles.chip, ...styles.chipCustomer }}>✓ Customer</span>
        {email && <span style={styles.chipSub} title={email}>{email}</span>}
      </span>
    );
  }
  return (
    <span style={styles.chipRow}>
      <span style={{ ...styles.chip, ...styles.chipVisitor }}>Visitor</span>
      <span style={styles.chipSub}>{session.visitor_phone || "Not signed in"}</span>
    </span>
  );
}

// Which queue the shopper asked for on the welcome screen (Sales / Customer care).
const DEPT_TAG = {
  1: { label: "Sales", color: "#ea1e51", bg: "#fde8ee" },
  2: { label: "Customer care", color: "#9f1239", bg: "#ffe4e6" },
};
function DeptTag({ session }) {
  const d = session && DEPT_TAG[session.department];
  if (!d) return null;
  return <span style={{ ...styles.deptTag, color: d.color, background: d.bg }}>{d.label}</span>;
}

// How many cart lines to show before "+N more" while the dock is at its normal size.
// Maximizing the dock (expanded) reveals every line.
const CART_PREVIEW_LIMIT = 6;

// Right-hand sidebar with the logged-in shopper's profile, ecom order history and live cart.
function CustomerInfoPanel({ session, ctx, expanded, currencySymbol = "" }) {
  const loading = ctx === undefined;
  const c = ctx && ctx.customer;
  const orders = (ctx && ctx.orders) || {};
  const cart = (ctx && ctx.cart) || {};
  const page = ctx && (ctx.page_title || ctx.page);
  const [cartOpen, setCartOpen] = useState(false); // full cart table overlay (handles 100+ lines)
  // Total prefers the live heartbeat value; falls back to the sum of line totals so it still
  // shows for an offline / abandoned cart (same source as the View-all overlay footer).
  const cartTotal = cartTotals(cart).total;
  return (
    <div style={styles.infoCol}>
      <div style={styles.infoSectionHead}>Customer</div>
      {loading && <div style={styles.infoMuted}>Loading…</div>}
      {!loading && !c && <div style={styles.infoMuted}>Profile unavailable.</div>}
      {!loading && c && (
        <>
          <div style={styles.infoName}>{c.business_name || c.name || displayName(session)}</div>
          {c.business_name && c.name && c.name !== c.business_name && (
            <div style={styles.nameSub}>{c.name}</div>
          )}
          {c.email && (
            <div style={styles.infoLineIcon}>
              <span style={styles.infoIcon}>✉</span>
              <span style={styles.infoText}>{c.email}</span>
            </div>
          )}
          {c.phone && (
            <div style={styles.infoLineIcon}>
              <span style={styles.infoIcon}>📞</span>
              <span style={styles.infoText}>{c.phone}</span>
            </div>
          )}
          {c.member_since && (
            <div style={styles.infoLineIcon}>
              <span style={styles.infoIcon}>📅</span>
              <span style={styles.infoText}>Member since {memberSince(c.member_since)}</span>
            </div>
          )}
          {c.balance != null && c.balance !== 0 && (
            <div style={styles.infoLineIcon}>
              <span style={styles.infoIcon}>💳</span>
              <span style={styles.infoText}>Balance {money(c.balance, currencySymbol)}</span>
            </div>
          )}

          <div style={styles.infoSectionHead}>
            Ecom orders {orders.count != null && <span style={styles.infoCount}>{orders.count}</span>}
          </div>
          {orders.lifetime_value != null && orders.count > 0 && (
            <div style={styles.infoLine}>Lifetime spend: {money(orders.lifetime_value, currencySymbol)}</div>
          )}
          {(orders.recent || []).length === 0 && <div style={styles.infoMuted}>No ecom orders yet.</div>}
          {(orders.recent || []).map((o) => (
            <div key={o.no || o.date} style={styles.orderRow}>
              <div style={styles.orderTop}>
                <span style={styles.orderNo}>{o.no || "—"}</span>
                <span style={styles.orderTotal}>{money(o.total, currencySymbol)}</span>
              </div>
              <div style={styles.orderMeta}>
                {ORDER_STATUS_LABEL[o.status] || "—"}
                {o.date ? ` · ${new Date(o.date).toLocaleDateString()}` : ""}
              </div>
            </div>
          ))}

          <div style={styles.infoSectionHead}>
            <span>Current cart</span>
            {(cart.items || []).length > 0 && (
              <button
                type="button"
                style={styles.cartExpandBtn}
                onClick={() => setCartOpen(true)}
                title="Open the full cart in a readable table"
              >
                ⤢ View all
              </button>
            )}
          </div>
          {(cart.count || (cart.items || []).length) ? (
            <>
              <div style={styles.infoLine}>
                {cart.count != null ? `${cart.count} item${cart.count === 1 ? "" : "s"}` : `${cart.items.length} items`}
              </div>
              {(expanded ? cart.items || [] : (cart.items || []).slice(0, CART_PREVIEW_LIMIT)).map((it, i) => {
                const unit = money(it.unit_price, currencySymbol);
                const lineTotal = it.unit_price != null ? money(it.unit_price * (it.qty || 0), currencySymbol) : null;
                const meta = [it.uom, unit != null ? `${unit}/unit` : null].filter(Boolean).join(" · ");
                return (
                  <div key={i} style={styles.cartRow}>
                    <div style={styles.cartItemTop}>
                      <span style={styles.cartName} title={it.name}>
                        {it.name}
                      </span>
                      <span style={styles.cartQty}>×{it.qty}</span>
                    </div>
                    {(meta || lineTotal != null) && (
                      <div style={styles.cartMeta}>
                        <span style={styles.cartName} title={meta}>
                          {meta || "—"}
                        </span>
                        {lineTotal != null && <span style={styles.cartLineTotal}>{lineTotal}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
              {!expanded && (cart.items || []).length > CART_PREVIEW_LIMIT && (
                <button type="button" style={styles.cartMoreBtn} onClick={() => setCartOpen(true)}>
                  +{(cart.items || []).length - CART_PREVIEW_LIMIT} more — view all
                </button>
              )}
              {cartTotal != null && (
                <div style={styles.cartTotalRow}>
                  <span>Cart total</span>
                  <span style={styles.cartTotalVal}>{money(cartTotal, currencySymbol)}</span>
                </div>
              )}
            </>
          ) : (
            <div style={styles.infoMuted}>Cart is empty.</div>
          )}

          {page && (
            <>
              <div style={styles.infoSectionHead}>Viewing</div>
              <div style={styles.infoLine} title={ctx.page || ""}>🧭 {page}</div>
            </>
          )}

          <CartDetailModal
            open={cartOpen}
            onClose={() => setCartOpen(false)}
            customerName={c.name || displayName(session)}
            cart={cart}
            currencySymbol={currencySymbol}
          />
        </>
      )}
    </div>
  );
}

// Totals for the cart overlay footer. Prefer the live heartbeat value; fall back to the sum of
// priced lines when offline (some lines may have no resolvable price → excluded from the sum).
function cartTotals(cart) {
  const items = cart.items || [];
  const totalQty = items.reduce((s, it) => s + (it.qty || 0), 0);
  let sum = 0;
  let anyPriced = false;
  items.forEach((it) => {
    if (it.unit_price != null) {
      sum += it.unit_price * (it.qty || 0);
      anyPriced = true;
    }
  });
  const total = cart.value != null ? cart.value : anyPriced ? sum : null;
  return { lines: items.length, totalQty, total };
}

// Full-cart overlay: a readable, scrollable table (Item · UOM · Unit price · Qty · Line total)
// for sales/support to scan every line — built to take 100+ items via a scrolling body with a
// sticky header. Reads the same ctx.cart the sidebar uses; the backend sends all lines uncapped.
function CartDetailModal({ open, onClose, customerName, cart, currencySymbol = "" }) {
  if (!open) return null;
  const items = cart.items || [];
  const { lines, totalQty, total } = cartTotals(cart);
  return (
    <div style={styles.cartModalOverlay} onClick={onClose} role="dialog" aria-modal="true" aria-label="Current cart">
      <div style={styles.cartModalCard} onClick={(e) => e.stopPropagation()}>
        <div style={styles.cartModalHead}>
          <div>
            <div style={styles.cartModalTitle}>Current cart</div>
            <div style={styles.cartModalSub}>{customerName}</div>
          </div>
          <button style={styles.cartModalClose} onClick={onClose} aria-label="Close" title="Close">
            ×
          </button>
        </div>
        <div style={styles.cartModalBody}>
          <table style={styles.cartTable}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: 40, textAlign: "right" }}>#</th>
                <th style={styles.th}>Item</th>
                <th style={styles.th}>UOM</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Unit price</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Qty</th>
                <th style={{ ...styles.th, ...styles.thRight }}>Line total</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td style={styles.td} colSpan={6}>
                    Cart is empty.
                  </td>
                </tr>
              )}
              {items.map((it, i) => {
                const unit = money(it.unit_price, currencySymbol);
                const lineTotal = it.unit_price != null ? money(it.unit_price * (it.qty || 0), currencySymbol) : null;
                return (
                  <tr key={i}>
                    <td style={{ ...styles.td, ...styles.tdRight, color: FAINT }}>{i + 1}</td>
                    <td style={styles.tdName}>{it.name}</td>
                    <td style={styles.td}>{it.uom || "—"}</td>
                    <td style={{ ...styles.td, ...styles.tdRight }}>{unit != null ? unit : "—"}</td>
                    <td style={{ ...styles.td, ...styles.tdRight }}>{it.qty}</td>
                    <td style={{ ...styles.td, ...styles.tdRight, fontWeight: 600, color: ACCENT_DEEP }}>
                      {lineTotal != null ? lineTotal : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={styles.cartModalFoot}>
          <span>
            {lines} line{lines === 1 ? "" : "s"} · {totalQty} item{totalQty === 1 ? "" : "s"}
          </span>
          {total != null && <span style={styles.cartModalTotal}>Total {money(total, currencySymbol)}</span>}
        </div>
      </div>
    </div>
  );
}

function bubbleStyle(side) {
  const base = { maxWidth: "80%", padding: "9px 13px", borderRadius: 16, marginBottom: 7, fontSize: 13, lineHeight: 1.45, wordBreak: "break-word" };
  if (side === "me")
    return {
      ...base,
      alignSelf: "flex-end",
      background: GRAD,
      color: "#fff",
      borderBottomRightRadius: 5,
      boxShadow: "0 5px 16px rgba(234,30,81,0.32)",
    };
  if (side === "customer")
    return {
      ...base,
      alignSelf: "flex-start",
      background: "rgba(255,255,255,0.92)",
      color: "#1e293b",
      border: "1px solid rgba(148,163,184,0.25)",
      borderBottomLeftRadius: 5,
      boxShadow: "0 2px 8px rgba(15,23,42,0.06)",
    };
  return { alignSelf: "center", fontSize: 11, color: "#94a3b8", fontStyle: "italic", margin: "6px 0", textAlign: "center" };
}

// --- Premium Gradient Glass design tokens ---
// Single source of truth for the dock's frosted-glass + brand-red gradient look.
const GRAD = "linear-gradient(135deg, #ea1e51 0%, #ff5277 100%)";
const GRAD_GREEN = "linear-gradient(135deg, #10b981 0%, #059669 100%)";
const ACCENT = "#ea1e51";
const ACCENT_DEEP = "#c01246";
const TEXT = "#1e293b";
const MUTED = "#64748b";
const FAINT = "#94a3b8";
const GLASS_BLUR = "blur(20px) saturate(180%)";
const SOFT_BLUR = "blur(8px)";
const GLASS_BORDER = "1px solid rgba(148,163,184,0.18)";
const GLOW = "0 6px 18px rgba(234,30,81,0.35)";

const styles = {
  // Bottom-left, but nudged right of the CS support chatbot launcher (KhubChatbot.css:
  // .khub-launcher is bottom:28/left:28, 48px). At left:96 the two bubbles sit side by side
  // with a ~20px gap instead of overlapping, and this FAB stays in the bottom row (below the
  // CS panel's bottom edge at 96px) so it never collides with the chatbot panel either.
  root: { position: "fixed", bottom: 20, left: 96, zIndex: 1050 },
  fab: { width: 56, height: 56, borderRadius: "50%", border: "none", background: GRAD, color: "#fff", fontSize: 24, cursor: "pointer", boxShadow: "0 10px 28px rgba(234,30,81,0.5)", position: "relative", transition: "transform 0.15s ease, box-shadow 0.15s ease" },
  badge: { position: "absolute", top: -2, right: -2, background: "#f43f5e", color: "#fff", borderRadius: 10, minWidth: 18, height: 18, fontSize: 11, lineHeight: "18px", padding: "0 5px", fontWeight: 700, boxShadow: "0 2px 8px rgba(244,63,94,0.55)" },
  // Online-customers count — green (presence convention), bottom-right so it never collides with the rose unread badge above.
  onlineBadge: { position: "absolute", bottom: -2, right: -2, background: "#10b981", color: "#fff", borderRadius: 10, minWidth: 18, height: 18, fontSize: 11, lineHeight: "18px", padding: "0 5px", fontWeight: 700, boxShadow: "0 2px 8px rgba(16,185,129,0.55)", border: "1.5px solid rgba(255,255,255,0.85)" },
  panel: { width: 540, height: 460, background: "rgba(255,255,255,0.78)", backdropFilter: GLASS_BLUR, WebkitBackdropFilter: GLASS_BLUR, borderRadius: 20, border: "1px solid rgba(255,255,255,0.65)", boxShadow: "0 24px 64px rgba(234,30,81,0.28), 0 4px 12px rgba(15,23,42,0.10)", display: "flex", flexDirection: "column", overflow: "hidden", marginBottom: 14, transition: "width 0.15s ease", position: "relative" },
  // Maximized: a large overlay so a long cart + full thread are readable. Anchored bottom-left
  // by `root`, so it grows up-and-right and never overlaps the FAB beneath it.
  panelMax: { width: "92vw", height: "88vh", maxWidth: 1180, maxHeight: 920 },
  header: { background: GRAD, color: "#fff", padding: "13px 16px", fontWeight: 700, fontSize: 15, letterSpacing: 0.2, display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 4px 16px rgba(234,30,81,0.28)" },
  close: { background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", fontSize: 17, cursor: "pointer", lineHeight: 1, borderRadius: 8, width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center" },
  headerActions: { display: "flex", alignItems: "center", gap: 6 },
  cols: { flex: 1, display: "flex", overflow: "hidden" },
  listCol: { width: 200, borderRight: GLASS_BORDER, overflowY: "auto", background: "rgba(255,255,255,0.35)" },
  // Dock search box (filter conversations / find a customer to start a chat with).
  searchBox: { padding: "10px 10px", borderBottom: GLASS_BORDER, position: "sticky", top: 0, background: "rgba(255,255,255,0.65)", backdropFilter: SOFT_BLUR, WebkitBackdropFilter: SOFT_BLUR, zIndex: 1 },
  searchInput: { width: "100%", border: "1px solid rgba(148,163,184,0.4)", borderRadius: 10, padding: "7px 11px", fontSize: 12, outline: "none", boxSizing: "border-box", background: "rgba(255,255,255,0.9)", color: TEXT },
  searchSection: { fontSize: 10, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, padding: "10px 12px 4px", borderTop: GLASS_BORDER },
  searchEmpty: { fontSize: 12, color: FAINT, padding: "10px 12px", fontStyle: "italic" },
  searchResultSub: { fontSize: 11, color: FAINT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 2 },
  startChatHint: { fontSize: 10, fontWeight: 700, color: ACCENT_DEEP, background: "rgba(234,30,81,0.1)", borderRadius: 6, padding: "1px 6px", flexShrink: 0 },
  listItem: { padding: "10px 12px", borderBottom: GLASS_BORDER, cursor: "pointer", transition: "background 0.12s ease" },
  listItemActive: { background: "rgba(234,30,81,0.10)" },
  listRow: { display: "flex", alignItems: "flex-start", gap: 8 },
  listBody: { flex: 1, minWidth: 0 },
  listTop: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  nameStack: { display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" },
  nameSub: { fontSize: 11, fontWeight: 400, color: MUTED, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  listName: { fontWeight: 600, fontSize: 13, color: TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  listPreview: { fontSize: 12, color: MUTED, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 2 },
  dot: { background: "#f43f5e", color: "#fff", borderRadius: 9, minWidth: 16, height: 16, fontSize: 10, lineHeight: "16px", textAlign: "center", padding: "0 4px", flexShrink: 0, fontWeight: 700, boxShadow: "0 2px 6px rgba(244,63,94,0.4)" },
  threadCol: { flex: 1, display: "flex", flexDirection: "column", minWidth: 0 },
  // Wraps when the thread column is narrow (info panel open): identity on line 1, actions drop
  // to line 2 instead of overlapping (space-between with overflowing children used to collide).
  threadHead: { padding: "10px 12px", borderBottom: GLASS_BORDER, display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 600, fontSize: 13, gap: 8, rowGap: 8, flexWrap: "wrap", background: "rgba(255,255,255,0.45)" },
  threadWho: { display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: "1 1 200px" },
  threadWhoText: { minWidth: 0, display: "flex", flexDirection: "column", gap: 2 },
  threadName: { fontWeight: 600, fontSize: 13, color: TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  threadActions: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0, flexWrap: "wrap", rowGap: 6, marginLeft: "auto" },
  infoBtn: { border: "1px solid rgba(148,163,184,0.4)", background: "rgba(255,255,255,0.7)", borderRadius: 8, padding: "3px 8px", cursor: "pointer", color: ACCENT, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", lineHeight: 1.2 },
  infoBtnActive: { background: "rgba(234,30,81,0.12)", borderColor: ACCENT, color: ACCENT_DEEP },
  endBtn: { border: "1px solid rgba(148,163,184,0.4)", background: "rgba(255,255,255,0.7)", borderRadius: 8, padding: "3px 11px", cursor: "pointer", fontSize: 12, color: MUTED },
  // Identity chip — the trust signal that separates a logged-in customer from a guest.
  chipRow: { display: "flex", alignItems: "center", gap: 6, marginTop: 2 },
  chip: { fontSize: 10, fontWeight: 700, borderRadius: 6, padding: "1px 6px", letterSpacing: 0.2, whiteSpace: "nowrap" },
  chipCustomer: { background: "#dcfce7", color: "#15803d" },
  chipVisitor: { background: "#f1f5f9", color: "#64748b" },
  chipSub: { fontSize: 11, color: FAINT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 180, minWidth: 0 },
  listChips: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", minWidth: 0 },
  deptTag: { fontSize: 10, fontWeight: 700, borderRadius: 6, padding: "1px 6px", whiteSpace: "nowrap", marginTop: 2 },
  // Right-hand customer context sidebar.
  // Wider when shown: the info panel grows leftward (thread shrinks to match) so the agent can
  // read the cart price/UOM lines comfortably while the customer's details are open.
  infoCol: { width: 330, flexShrink: 0, borderLeft: GLASS_BORDER, overflowY: "auto", padding: "10px 12px", background: "rgba(255,255,255,0.4)" },
  infoSectionHead: { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, margin: "12px 0 6px", display: "flex", alignItems: "center", gap: 6 },
  infoCount: { background: "rgba(234,30,81,0.12)", color: ACCENT_DEEP, borderRadius: 8, fontSize: 10, padding: "0 6px", lineHeight: "15px", fontWeight: 700 },
  infoName: { fontSize: 14, fontWeight: 600, color: TEXT },
  infoLine: { fontSize: 12, color: "#475569", marginTop: 4, wordBreak: "break-word" },
  // Icon line: a fixed-width, centered glyph column so the ✉ / 📞 / 📅 / 💳 marks line up with their text.
  infoLineIcon: { fontSize: 12, color: "#475569", marginTop: 4, display: "flex", alignItems: "center", gap: 6 },
  infoIcon: { flexShrink: 0, width: 16, textAlign: "center", fontSize: 13, lineHeight: 1 },
  infoText: { minWidth: 0, wordBreak: "break-word" },
  infoMuted: { fontSize: 12, color: FAINT, marginTop: 4, fontStyle: "italic" },
  orderRow: { borderTop: GLASS_BORDER, padding: "6px 0" },
  orderTop: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  orderNo: { fontSize: 12, fontWeight: 600, color: "#334155" },
  orderTotal: { fontSize: 12, fontWeight: 700, color: ACCENT_DEEP },
  orderMeta: { fontSize: 11, color: FAINT, marginTop: 1 },
  // Cart line: product name + qty on top, UOM · price/unit + line total beneath.
  cartRow: { marginTop: 6, paddingBottom: 6, borderBottom: "1px dashed rgba(148,163,184,0.22)" },
  cartItemTop: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "#475569", gap: 8 },
  cartMeta: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, color: FAINT, gap: 8, marginTop: 2 },
  cartLineTotal: { fontWeight: 600, color: ACCENT_DEEP, flexShrink: 0 },
  cartName: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 },
  cartQty: { color: FAINT, flexShrink: 0 },
  // "View all" affordances → the full-cart overlay.
  cartExpandBtn: { marginLeft: "auto", border: "1px solid rgba(234,30,81,0.4)", background: "rgba(234,30,81,0.06)", color: ACCENT_DEEP, borderRadius: 8, padding: "2px 8px", cursor: "pointer", fontSize: 10, fontWeight: 700, letterSpacing: 0.2, textTransform: "none", flexShrink: 0 },
  cartMoreBtn: { border: "none", background: "none", color: ACCENT_DEEP, cursor: "pointer", fontSize: 11, fontWeight: 700, marginTop: 6, padding: 0, textAlign: "left" },
  // Cart total row — sits under the line items, mirrors the View-all overlay footer.
  cartTotalRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(148,163,184,0.35)", fontSize: 13, fontWeight: 700, color: TEXT },
  cartTotalVal: { color: ACCENT_DEEP, fontSize: 15 },
  // Full-cart overlay (covers the dock; readable table that takes 100+ lines via a scrolling body).
  cartModalOverlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", backdropFilter: SOFT_BLUR, WebkitBackdropFilter: SOFT_BLUR, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 40 },
  cartModalCard: { width: "min(960px, 94vw)", background: "#fff", borderRadius: 16, boxShadow: "0 24px 64px rgba(15,23,42,0.35)", display: "flex", flexDirection: "column", overflow: "hidden" },
  cartModalHead: { background: GRAD, color: "#fff", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 },
  cartModalTitle: { fontWeight: 700, fontSize: 15, letterSpacing: 0.2 },
  cartModalSub: { fontSize: 12, opacity: 0.92, marginTop: 2 },
  cartModalClose: { background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", fontSize: 20, cursor: "pointer", borderRadius: 8, width: 30, height: 30, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  cartModalBody: { overflowY: "auto", maxHeight: 300, padding: "0 12px" },
  cartTable: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { position: "sticky", top: 0, background: "#fff", textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "2px solid rgba(148,163,184,0.25)", zIndex: 1 },
  thRight: { textAlign: "right" },
  td: { padding: "9px 14px", borderBottom: "1px solid rgba(148,163,184,0.15)", color: "#334155", verticalAlign: "top" },
  tdName: { padding: "9px 14px", borderBottom: "1px solid rgba(148,163,184,0.15)", color: TEXT, fontWeight: 500, maxWidth: 360, wordBreak: "break-word" },
  tdRight: { textAlign: "right", whiteSpace: "nowrap" },
  cartModalFoot: { borderTop: "1px solid rgba(148,163,184,0.25)", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, color: MUTED, background: "rgba(248,250,252,0.85)", flexShrink: 0 },
  cartModalTotal: { fontWeight: 700, color: ACCENT_DEEP, fontSize: 15 },
  thread: { flex: 1, padding: 14, overflowY: "auto", display: "flex", flexDirection: "column" },
  empty: { color: FAINT, fontSize: 13, margin: "auto", padding: 20, textAlign: "center" },
  // Day separator + per-bubble time.
  dateChip: { alignSelf: "center", fontSize: 10, fontWeight: 600, color: MUTED, background: "rgba(255,255,255,0.75)", border: GLASS_BORDER, borderRadius: 12, padding: "2px 12px", margin: "8px 0 6px", backdropFilter: SOFT_BLUR, WebkitBackdropFilter: SOFT_BLUR },
  msgTime: { fontSize: 10, color: FAINT, textAlign: "right", marginTop: 3, lineHeight: 1 },
  msgTimeMe: { color: "rgba(255,255,255,0.8)" },
  // Two-row composer (Tidio-style): full-width message box on top, tools + round send below.
  composer: { display: "flex", flexDirection: "column", borderTop: GLASS_BORDER, padding: "10px 12px 12px", position: "relative", background: "rgba(255,255,255,0.55)", backdropFilter: SOFT_BLUR, WebkitBackdropFilter: SOFT_BLUR },
  input: { width: "100%", border: "none", padding: "6px 2px", fontSize: 13, outline: "none", boxSizing: "border-box", background: "transparent", color: TEXT },
  toolbar: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 },
  tools: { display: "flex", alignItems: "center", gap: 2 },
  iconBtn: { border: "none", background: "none", fontSize: 18, cursor: "pointer", padding: "4px 5px", lineHeight: 1, borderRadius: 8, opacity: 0.65 },
  send: { border: "none", background: GRAD, color: "#fff", borderRadius: "50%", width: 36, height: 36, cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: GLOW },
  mediaError: { color: "#e11d48", fontSize: 12, padding: "4px 12px 0" },
  endedNote: { borderTop: GLASS_BORDER, padding: "12px 14px", textAlign: "center", color: MUTED, fontSize: 12, fontStyle: "italic" },
  // "Reopen chat" under the ended note — re-pings the shopper in the same thread.
  reopenBtn: { border: "none", background: GRAD, color: "#fff", borderRadius: 10, padding: "7px 18px", cursor: "pointer", fontSize: 12, fontWeight: 600, marginTop: 8, fontStyle: "normal", boxShadow: GLOW },
  // Bottom tab bar (Chats | Shoppers) + the Shoppers tab list.
  tabBar: { display: "flex", borderTop: GLASS_BORDER, flexShrink: 0, background: "rgba(255,255,255,0.45)" },
  tabBtn: { flex: 1, border: "none", background: "transparent", padding: "10px 0", fontSize: 12, fontWeight: 600, color: MUTED, cursor: "pointer" },
  tabBtnActive: { color: ACCENT_DEEP, background: "rgba(255,255,255,0.6)", boxShadow: "inset 0 2px 0 #ea1e51" },
  shoppersCol: { flex: 1, overflowY: "auto" },
  cartLine: { fontSize: 12, color: ACCENT_DEEP, fontWeight: 600, marginTop: 2 },
  // First-time-customer preview pane (search result picked, no conversation yet).
  draftHint: { margin: "auto", textAlign: "center", color: MUTED, fontSize: 12, lineHeight: 1.5, padding: "0 24px" },
  draftHintTitle: { fontWeight: 600, color: "#334155", marginBottom: 4, fontSize: 13 },
  initiateBtn: { border: "none", background: GRAD, color: "#fff", borderRadius: 10, padding: "8px 18px", cursor: "pointer", fontSize: 13, fontWeight: 600, boxShadow: GLOW },
  initiateBtnBusy: { opacity: 0.6, cursor: "default", boxShadow: "none" },
  // Shown instead of the composer when the shopper started a new chat in this thread
  // and it hasn't been claimed yet.
  acceptBar: { borderTop: GLASS_BORDER, padding: "10px 14px", display: "flex", justifyContent: "center" },
  acceptBtn: { border: "none", background: GRAD_GREEN, color: "#fff", borderRadius: 10, padding: "8px 18px", cursor: "pointer", fontSize: 13, fontWeight: 600, boxShadow: "0 6px 18px rgba(16,185,129,0.4)" },
  // Shown instead of the composer when a colleague owns the active session.
  handledBar: { borderTop: GLASS_BORDER, padding: "12px 14px", textAlign: "center", color: MUTED, fontSize: 12, background: "rgba(255,255,255,0.5)" },
  // Private agent-to-agent note that rode along with a transfer (never persisted).
  noteBanner: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, background: "rgba(255,247,230,0.92)", borderBottom: "1px solid #fde9c8", color: "#92600a", fontSize: 12, padding: "8px 12px" },
  noteDismiss: { border: "none", background: "none", color: "#92600a", cursor: "pointer", fontSize: 15, lineHeight: 1, padding: 0, flexShrink: 0 },
  // Transfer modal (overlay scoped to the dock panel).
  modalOverlay: { position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(60,10,25,0.5)", backdropFilter: SOFT_BLUR, WebkitBackdropFilter: SOFT_BLUR, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5 },
  modal: { width: 340, maxHeight: "85%", background: "rgba(255,255,255,0.95)", backdropFilter: GLASS_BLUR, WebkitBackdropFilter: GLASS_BLUR, borderRadius: 16, border: "1px solid rgba(255,255,255,0.6)", boxShadow: "0 24px 64px rgba(60,10,25,0.4)", padding: 16, display: "flex", flexDirection: "column", gap: 10, boxSizing: "border-box" },
  modalTitle: { fontWeight: 700, fontSize: 14, color: TEXT },
  modalSub: { fontSize: 12, color: MUTED, lineHeight: 1.45 },
  modalList: { maxHeight: 160, overflowY: "auto", border: GLASS_BORDER, borderRadius: 10 },
  agentRow: { display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", cursor: "pointer", borderBottom: GLASS_BORDER },
  agentRowActive: { background: "rgba(234,30,81,0.10)" },
  agentDot: { width: 9, height: 9, borderRadius: "50%", background: "#22c55e", flexShrink: 0 },
  agentDotBusy: { background: "#f59e0b" },
  agentName: { flex: 1, fontSize: 13, fontWeight: 600, color: TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  busyPill: { fontSize: 10, fontWeight: 700, color: "#92600a", background: "#fef3c7", borderRadius: 6, padding: "1px 6px", flexShrink: 0 },
  agentLoad: { fontSize: 11, color: FAINT, flexShrink: 0 },
  noteInput: { width: "100%", border: "1px solid rgba(148,163,184,0.4)", borderRadius: 10, padding: "7px 10px", fontSize: 12, outline: "none", boxSizing: "border-box", background: "rgba(255,255,255,0.9)" },
  busyWarning: { fontSize: 12, color: "#92600a", background: "#fff7e6", border: "1px solid #fde9c8", borderRadius: 8, padding: "8px 10px", lineHeight: 1.4 },
  modalActions: { display: "flex", justifyContent: "flex-end", gap: 8 },
  modalGhostBtn: { border: "1px solid rgba(148,163,184,0.45)", background: "rgba(255,255,255,0.8)", borderRadius: 10, padding: "6px 12px", cursor: "pointer", fontSize: 12, color: MUTED },
  transferAnywayBtn: { background: "#d97706", boxShadow: "0 6px 18px rgba(217,119,6,0.4)" },
  // --- voice call ---
  // "Call" button in the thread header — greener tint when callable, plain endBtn otherwise.
  callBtn: { border: "1px solid rgba(16,185,129,0.5)", background: "rgba(16,185,129,0.12)", color: "#047857" },
  // "Shopper requested a call" chip above the thread.
  callReqBanner: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "rgba(16,185,129,0.10)", borderBottom: "1px solid rgba(16,185,129,0.25)", color: "#047857", fontSize: 12, fontWeight: 600, padding: "8px 12px" },
  callReqActions: { display: "flex", alignItems: "center", gap: 8 },
  callReqBtn: { border: "none", background: GRAD_GREEN, color: "#fff", borderRadius: 10, padding: "5px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600, boxShadow: "0 4px 12px rgba(16,185,129,0.35)" },
  // Non-blocking in-call bar (slim strip under the header) — the dock stays fully usable.
  callBar: { display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", background: "rgba(16,185,129,0.12)", borderBottom: "1px solid rgba(16,185,129,0.28)", flexShrink: 0 },
  // Same strip as a floating pill above the launcher when the dock is minimized.
  callBarFloating: { display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", width: 300, boxSizing: "border-box", background: "rgba(255,255,255,0.97)", backdropFilter: GLASS_BLUR, WebkitBackdropFilter: GLASS_BLUR, border: "1px solid rgba(255,255,255,0.6)", borderRadius: 14, boxShadow: "0 14px 34px rgba(16,185,129,0.32)", marginBottom: 12 },
  callBarIcon: { fontSize: 18, flexShrink: 0 },
  callBarText: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column" },
  callBarName: { fontSize: 13, fontWeight: 700, color: TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  callBarStatus: { fontSize: 11, fontWeight: 600, color: "#047857" },
  callBarActions: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0 },
  callBarMute: { border: "1px solid rgba(148,163,184,0.45)", background: "rgba(255,255,255,0.85)", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 13, lineHeight: 1.2 },
  callBarEnd: { border: "none", background: "#e11d48", color: "#fff", borderRadius: 8, padding: "5px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 12px rgba(225,29,72,0.35)" },
  callBarDismiss: { border: "1px solid rgba(148,163,184,0.45)", background: "rgba(255,255,255,0.85)", borderRadius: 8, padding: "5px 12px", fontSize: 12, color: MUTED, cursor: "pointer" },
  callMuteOn: { background: "#fef3c7", borderColor: "#fcd34d", color: "#92600a" },
};
